<template>
  <div class="filter-wrapper studio-new">
    <v-form>
      <div class="studio-top-heading pb-2">
        <div class="plot-header-row">
          <h6 class="plot-title">Plot</h6>
          <v-img
            v-if="selectedSavedChartName != null"
            :src="dash_create"
            alt="create"
            width="89"
            height="32"
            class="set-cursor create-btn-compact"
            @click="resetChartData"
          />
        </div>
      </div>
      
      <v-row no-gutters>
        <v-col cols="12" class="studio-input-form" v-if="isFluidsDataStudio || isDashboard">
          <v-row no-gutters style="width: 100%;">
            <!-- Plot Title removed - now handled in top bar -->
            
            <!-- Test Type -->
            <v-col cols="12" class="pa-0">
              <label for="">Test Type</label>
              <v-select variant="solo" class="custom-field" v-model="selectedTableTypeName"
                :items="filteredTableTypesNames" item-title="title" item-value="value" 
                placeholder="Test Type" @update:modelValue="tableTypeChange"
                hide-details>
                <!-- Search Input + loader -->
                <template #prepend-item>
                  <v-text-field variant="solo" class="custom-field mx-4" v-model="tableTypeSearchQuery"
                    placeholder="Test Type" dense outlined clearable @input="filterTableTypeList"
                    @click:clear="resetTableTypeList" hide-details></v-text-field>
                  <div class="loader-container" v-if="loadingTableType">
                    <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                  </div>
                </template>
                <template #no-data>
                  <v-list-item v-if="!loadingTableType">
                    <v-list-item-content>No data available</v-list-item-content>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="text-truncate">{{ item.title }}</span>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
                <template #item="{ props, item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props: tooltipProps }">
                      <div v-bind="props" class="dropdown-item" @click="props.onClick">
                        <span v-bind="tooltipProps" class="text-truncate">{{ item.title }}</span>
                      </div>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
            
            <!-- Plot Type -->
            <v-col cols="12" class="pa-0">
              <label for="">Plot Type</label>
              <v-select variant="solo" class="custom-field" v-model="selectedChartTypeName"
                :items="filteredChartTypesNames" item-title="title" item-value="value"
                placeholder="Plot Type" @update:modelValue="chartTypeChange"
                :disabled="selectedTableTypeName === null" hide-details>
                <!-- Search Input + loader -->
                <template #prepend-item>
                  <v-text-field variant="solo" class="custom-field mx-4" v-model="chartTypeSearchQuery"
                    placeholder="Plot Type" dense outlined clearable @input="filterChartTypeList"
                    @click:clear="resetChartTypeList" hide-details></v-text-field>
                  <div class="loader-container" v-if="loadingChartType">
                    <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                  </div>
                </template>
                <template #no-data>
                  <v-list-item v-if="!loadingChartType">
                    <v-list-item-content>No data available</v-list-item-content>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="text-truncate">{{ item.title }}</span>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
                <template #item="{ props, item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props: tooltipProps }">
                      <div v-bind="props" class="dropdown-item" @click="props.onClick">
                        <span v-bind="tooltipProps" class="text-truncate">{{ item.title }}</span>
                      </div>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
            
            <!-- X-Axis Selection -->
            <v-col cols="12" class="px-0 pt-2 pb-0" v-if="showXAxis">
              <div class="d-flex align-center justify-space-between">
                <label for="">X-Axis Column</label>
                <div v-if="selectedChartTypeName === 'Line Plot' || selectedChartTypeName === 'Scatter Plot'" class="tab-div justify-space-between">
                  <v-btn-toggle v-model="isXLog" class="axis-toggle" mandatory @update:modelValue="handleXScaleToggleChange">
                    <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                    <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                  </v-btn-toggle>
                </div>
              </div>
              <v-select variant="solo" class="custom-field" v-model="selectedXAxisKey"
                :items="filteredXAxisItems" item-title="displayName" item-value="name"
                placeholder="X-Axis Column" @update:modelValue="handleXSelection" hide-details>
                <!-- Search Input + loader -->
                <template #prepend-item>
                  <v-text-field variant="solo" class="custom-field mx-4" v-model="xAxisSearchQuery"
                    placeholder="Search X-Axis Column" dense outlined clearable @input="filterXAxisList"
                    @click:clear="resetXAxisList" hide-details></v-text-field>
                  <div class="loader-container" v-if="loadingXAxisData">
                    <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                  </div>
                </template>
                <template #no-data>
                  <v-list-item v-if="!loadingXAxisData">
                    <v-list-item-content>No data available</v-list-item-content>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="text-truncate">{{ item.title }}</span>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
                <template #item="{ props, item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props: tooltipProps }">
                      <div v-bind="props" class="dropdown-item" @click="props.onClick">
                        <span v-bind="tooltipProps" class="text-truncate">{{ item.title }}</span>
                      </div>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
            
            <!-- Y-Axis Selection -->
            <v-col cols="12" class="px-0 py-2" v-if="showYAxis">
              <div class="d-flex align-center justify-space-between">
                <label for="">Y-Axis Column(s)</label>
                <div v-if="selectedChartTypeName === 'Line Plot' || selectedChartTypeName === 'Scatter Plot'" class="tab-div justify-space-between">
                  <v-btn-toggle v-model="isYLog" class="axis-toggle" mandatory @update:modelValue="handleYScaleToggleChange">
                    <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                    <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                  </v-btn-toggle>
                </div>
              </div>
              <v-select variant="solo" v-model="selectedYAxisKey" :items="filteredYAxisItems" multiple chips clearable
                closable-chips placeholder="Y-Axis Column(s)" item-title="displayName" item-value="name"
                :return-object="false" @update:modelValue="handleYSelection" class="custom-y-axis-chips custom-field" hide-details>
                <!-- Search Input + loader -->
                <template #prepend-item>
                  <v-text-field variant="solo" class="custom-field mx-4" v-model="yAxisSearchQuery"
                    placeholder="Search Y-Axis Column" dense outlined clearable @input="filterYAxisList"
                    @click:clear="resetYAxisList" hide-details></v-text-field>
                  <div class="loader-container" v-if="loadingYAxisData">
                    <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                  </div>
                </template>
                <template #no-data>
                  <v-list-item v-if="!loadingYAxisData">
                    <v-list-item-content>No data available</v-list-item-content>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="text-truncate">{{ item.title }}</span>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
                <template #item="{ props, item }">
                  <MultiselectCheckboxItem
                    :item-props="props"
                    :title="item.title"
                    :selected-values="selectedYAxisKey"
                    :item-value="item.value"
                  />
                </template>
              </v-select>
            </v-col>
            
            <!-- Z-Axis Selection -->
            <v-col cols="12" class="pa-0" v-if="showZAxis">
              <label for="">Z-Axis Column</label>
              <v-select variant="solo" class="custom-field" v-model="selectedZAxisKey" placeholder="Z-Axis Column"
                :items="filteredZAxisItems" item-title="displayName" item-value="name" @update:modelValue="handleZSelection" hide-details>
                <!-- Search Input + loader -->
                <template #prepend-item>
                  <v-text-field v-model="zAxisSearchQuery" placeholder="Search Z-Axis Column" dense outlined clearable
                    @input="filterZAxisList" @click:clear="resetZAxisList" hide-details></v-text-field>
                  <div class="loader-container" v-if="loadingZAxisData">
                    <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                  </div>
                </template>
                <template #no-data>
                  <v-list-item v-if="!loadingZAxisData">
                    <v-list-item-content>No data available</v-list-item-content>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="text-truncate">{{ item.title }}</span>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
                <template #item="{ props, item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props: tooltipProps }">
                      <div v-bind="props" class="dropdown-item" @click="props.onClick">
                        <span v-bind="tooltipProps" class="text-truncate">{{ item.title }}</span>
                      </div>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
            
            <!-- Color Group/Legend Selection -->
            <v-col cols="12" class="pa-0" v-if="showColorGroup">
              <label for="">Legend</label>
              <v-select variant="solo" class="custom-field" v-model="selectedColorGroupKey" clearable placeholder="Legend"
                :items="filteredColorGroupItems" item-title="displayName" item-value="name" @update:modelValue="handleColorGroupSelection" hide-details>
                <!-- Search Input + loader -->
                <template #prepend-item>
                  <v-text-field v-model="colorGroupSearchQuery" placeholder="Search Legend" dense outlined clearable
                    @input="filterColorGroupList" @click:clear="resetColorGroupList" hide-details></v-text-field>
                  <div class="loader-container" v-if="loadingZAxisData">
                    <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                  </div>
                </template>
                <template #no-data>
                  <v-list-item v-if="!loadingZAxisData">
                    <v-list-item-content>No data available</v-list-item-content>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="text-truncate">{{ item.title }}</span>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
                <template #item="{ props, item }">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props: tooltipProps }">
                      <div v-bind="props" class="dropdown-item" @click="props.onClick">
                        <span v-bind="tooltipProps" class="text-truncate">{{ item.title }}</span>
                      </div>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
            
            <!-- X-Bins Input -->
            <v-col cols="12" class="pa-0" v-if="showXBinsInput">
              <label for="">Number of X bins</label>
              <div id="rangeValue">{{ xBinsAsNumber }}</div>
              <input type="range" id="rangeInput" class="slider" min="2" max="100" step="1" v-model="xBins" @input="updateXBins" />
            </v-col>
            
            <!-- Y-Bins Input -->
            <v-col cols="12" class="pa-0" v-if="showYBinsInput">
              <label for="">Number of Y bins</label>
              <div id="rangeValue">{{ yBinsAsNumber }}</div>
              <input type="range" id="rangeInput" class="slider" min="2" max="100" step="1" v-model="yBins" @input="updateYBins" />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      
      <!-- Additional components -->
      <v-col v-if="$route.name === 'DataInventoryAnalysis'" cols="12" class="pa-0">
        <label for="">Select Saved Dashboard</label>
        <v-select v-model="dashboard" placeholder="Select Saved Dashboard" :items="dashboardItems"
          @update:modelValue="handleDshboardSelection">
          <template #selection="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <span v-bind="props" class="text-truncate">{{ item.title }}</span>
              </template>
              <span>{{ item.title }}</span>
            </v-tooltip>
          </template>
          <template #item="{ props, item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props: tooltipProps }">
                <div v-bind="props" class="dropdown-item" @click="props.onClick">
                  <span v-bind="tooltipProps" class="text-truncate">{{ item.title }}</span>
                </div>
              </template>
              <span>{{ item.title }}</span>
            </v-tooltip>
          </template>
        </v-select>
      </v-col>
      
      <v-snackbar v-model="snackbar.visible" :color="snackbar.color" :timeout="snackbar.timeout">
        {{ snackbar.message }}
      </v-snackbar>
    </v-form>
    
    <!-- Dialogs -->
    <v-dialog transition="dialog-top-transition" v-model="confirmDeleteDialog" width="417">
      <div class="cross-btn-div" @click="closeDeleteConfirmation">
        <v-img :src="cross" alt="close"></v-img>
      </div>
      <v-alert title="Delete Plot" :text=confirmDeleteText>
        <div class="alert-btns-div">
          <v-btn class="cancel-btn" flat @click="closeDeleteConfirmation">Cancel</v-btn>
          <v-btn class="delete-btn" flat @click="deleteChart" :loading="deleteLoading">
            <v-img :src="delete_icon" alt="delete-icon" width="16" height="15" class="mr-2"></v-img>
            Delete Plot</v-btn>
        </div>
      </v-alert>
    </v-dialog>
    <v-dialog transition="dialog-top-transition" v-model="confirmUpdateDialog" width="417">
      <div class="cross-btn-div" @click="closeUpdateConfirmation">
        <v-img :src="cross" alt="close"></v-img>
      </div>
      <v-alert title="Update Plot" :text=confirmUpdateText>
        <div class="alert-btns-div">
          <v-btn class="cancel-btn" flat @click="closeUpdateConfirmation">Cancel</v-btn>
          <v-btn
            v-if="selectedSavedChartName != null && (accessLevel == 'private' || (accessLevel == 'public' && isAdmin))"
            flat :disabled="!isUpdateEnabled" :loading="updateLoading" class="save-btn text-none update-btn"
            @click="updateChart">
            <div class="save-btn-img ml-2">
              <v-img src="../../../assets/Images/save btn.png" alt="update"></v-img>
            </div>
            <span>Update</span>
          </v-btn>
        </div>
      </v-alert>
    </v-dialog>
    <StudioModal ref="childComponent" @open-dialog="openDialog" />
    <Filters ref="filters" v-if="isFluidsDataStudio && !embedded" @drawerStateChanged="handleRightDrawerStateChange"
      :callDefaultFilter="callDefaultFilter" :selectedType="selectedTableTypeName" :filterFromChart="filterFromChart"
      @filter-data="onFilterDataChanged" @reset-saved-filter="handleResetSavedFilter"
      @reset-call-default-filter="resetCallDefaultFilter" @update-chart="callUpdateChartMethod"
      @drawerValue="toggleInputs" />
  </div>
</template>

<script>
import api from "../../../axiosInterceptor";
import Filters from "../Common/Filters.vue";
import MultiselectCheckboxItem from "../Common/MultiselectCheckboxItem.vue";
import screenWidthMixin from "../../../assets/JS/screenWidthMixin";
import StudioModal from "./StudioModal.vue";
import { mapGetters } from "vuex";
import { eventBus } from "../../../eventBus.js";
import {
  buildSelectColumnsFromState,
  buildYAxisColumnString,
  buildYAxisColumnsArray,
  getStoredChartData,
  isRequestAborted,
  mergeChartMetadataAfterUpdate,
  parseTableSamples,
  parseYAxisKeysFromChart,
  resolveColumnDisplayName,
  resolveYAxisDisplayNames,
} from "../../../utils/chartAxisUtils";
export default {
  name: "StudioDataLeft",
  components: { Filters, StudioModal, MultiselectCheckboxItem },
  data() {
    return {
      cross: require("../../../assets/Images/Buttons/cross.png"),
      delete_icon: require("../../../assets/Svg/trash.svg"),
      saveIcon: require("../../../assets/Svg/save.svg"),
      confirmDeleteDialog: false,
      confirmUpdateDialog: false,
      searchQuery: "",
      chartTypes: [],
      tableTypes: [],
      internalChartTitle: "",
      chartType: "", // Remove the prop reference, initialize as empty string
      xAxisItems: [],
      yAxisItems: [],
      zAxisItems: [],
      colorGroupItems: [],
      selectedXAxisKey: [],
      selectedYAxisKey: [],
      selectedZAxisKey: [],
      selectedColorGroupKey: null,
      xAxisData: [],
      yAxisData: [],
      zAxisData: [],
      colorGroupData: [],
      showXBinsInput: false,
      showXAxis: true,
      showYAxis: true,
      showZAxis: false,
      showColorGroup: false,
      xBins: 20,
      showYBinsInput: false,
      yBins: 20,
      chartTypesNames: [],
      tableTypesNames: [],
      selectedChartTypeName: null,
      selectedTableTypeName: null,
      selectedSavedChartName: null,
      savedCharts: [],
      savedChartsNames: [],
      tableData: [],
      showLoaders: false,
      deleteError: null,
      showTooltip: false,
      snackbar: {
        visible: false,
        message: "",
        color: "success",
        timeout: 3000,
      },
      createLoading: false,
      updateLoading: false,
      deleteLoading: false,
      loadingSavedCharts: false,
      loadingXAxisData: false,
      loadingYAxisData: false,
      loadingZAxisData: false,
      loadingTableType: false,
      loadingChartType: false,
      createNewChart: false,
      chartSaved: false,
      accessLevel: "private",
      isPublic: false,
      resetSavedFilter: false,
      callDefaultFilter: false,
      icon: require("../../../assets/Images//arrow-down.png"),
      icon2: require("../../../assets/Images/arrow-up.png"),
      showInputs: true,
      savedFilterData: [],
      savedType: "",
      colorGroupChanged: false,
      xAxisChanged: false,
      yAxisChanged: false,
      zAxisChanged: false,
      axisSelected: false,
      sampleIdPresent: false,
      filterFromChart: [],
      chartSearchQuery: "",
      tableTypeSearchQuery: "",
      chartTypeSearchQuery: "",
      filteredChartsNames: [],
      filteredTableTypesNames: [],
      filteredChartTypesNames: [],
      onlyApiCall: false,
      filterReset: false,
      filteredXAxisItems: [],
      filteredYAxisItems: [],
      filteredZAxisItems: [],
      filteredColorGroupItems: [],
      xAxisSearchQuery: "",
      yAxisSearchQuery: "",
      zAxisSearchQuery: "",
      colorGroupSearchQuery: "",
      xAxisAbortController: null,
      yAxisAbortController: null,
      zAxisAbortController: null,
      colorGroupAbortController: null,
      xAxisScale: "linear",
      yAxisScale: "linear",
      isXLog: false,
      isYLog: false,
      selectedChartId: null,
      confirmDeleteText: null,
      isUpdateEnabled: false,
      xAxisDisplayName: "",
      yAxisDisplayNames: [],
      zAxisDisplayName: "",
      colorGroupDisplayName: "",
      xAxisCaption: [],
      yAxisCaption: [],
      currentApiCallId: null,
      dataChunkSize: 1000, // Process 1000 rows at a time
      isProcessingLargeData: false,
      processedDataCount: 0,
      colorGroupSelectionTimeout: null,
      filterFetchTimer: null,
      tableTypeAbortController: null,
      chartTypeAbortController: null,
      dataFetchAbortController: null,
      chartUsageAbortController: null,
      chartDataAbortController: null,
      isUserSelecting: false,
      isPopulatingEditData: false,
    };
  },
  props: {
    selectedChartType: {
      type: String,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    editPlotData: {
      type: Object,
      default: null,
    },
    chartTitle: {
      type: String,
      default: '',
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [screenWidthMixin],
  computed: {
    ...mapGetters("tenantId", ["getUser"]),
    ...mapGetters("darkMode", ["isDarkMode"]),
    dash_create() {
      return this.isDarkMode
        ? require("../../../assets/Svg/Dashboard/create-btn-stu-dark.svg")
        : require("../../../assets/Svg/Dashboard/create-btn-stu.svg");
    },
    isAdmin() {
      return this.getUser && (this.getUser.role === "Administrator" || this.getUser.role === "SuperAdmin");
    },
    userFullName() {
      if (this.getUser && this.getUser.firstName && this.getUser.lastName) {
        return `${this.getUser.firstName} ${this.getUser.lastName}`;
      }
      return this.getUser?.username || '';
    },
    isFluidsDataStudio() {
      if (this.embedded) {
        return true;
      }
      const pathSegments = this.$route.path.split('/').filter(Boolean); // Remove empty segments
      return pathSegments.length > 1 && pathSegments[1].toLowerCase() === 'fluidsdata-studio';
    },

    isDashboard() {
      const pathSegments = this.$route.path.split('/').filter(Boolean); // Remove empty segments
      return (
        pathSegments.length > 2 &&
        (pathSegments[1].toLowerCase() === 'dashboards' && pathSegments[2].toLowerCase() === 'create') ||
        (pathSegments[1].toLowerCase() === 'dashboards' && pathSegments[2].toLowerCase() === 'edit')
      );
    },
    updateXBins() {
      this.$emit("update-x-bins", Number(this.xBins));
      let savedChartData = JSON.parse(localStorage.getItem("savedChartData")) || JSON.parse(localStorage.getItem("newsavedChartData"));
      if (savedChartData?.NumXBins) {
        savedChartData.NumXBins = Number(this.xBins);
        if (localStorage.getItem("savedChartData")) {
          localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
        } else {
          localStorage.setItem("newsavedChartData", JSON.stringify(savedChartData));
        }
      }
      this.saveChartData();
    },
    updateYBins() {
      this.$emit("update-y-bins", Number(this.yBins));
      let savedChartData = JSON.parse(localStorage.getItem("savedChartData")) || JSON.parse(localStorage.getItem("newsavedChartData"));
      if (savedChartData?.NumYBins) {
        savedChartData.NumYBins = Number(this.yBins);
        if (localStorage.getItem("savedChartData")) {
          localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
        } else {
          localStorage.setItem("newsavedChartData", JSON.stringify(savedChartData));
        }
      }
      this.saveChartData();
    },
    xBinsAsNumber() {
      return Number(this.xBins);
    },
    yBinsAsNumber() {
      return Number(this.yBins);
    },
    isFormValid() {
      const isBasicFieldsFilled =
        this.selectedChartTypeName &&
        this.internalChartTitle &&
        this.selectedTableTypeName;
      
      const isXAxisFilled = !this.showXAxis || (this.selectedXAxisKey && this.selectedXAxisKey.length > 0);
      
      const isYAxisFilled =
        !this.showYAxis ||
        (Array.isArray(this.selectedYAxisKey) &&
          this.selectedYAxisKey.length > 0);
      
      const isZAxisFilled = !this.showZAxis || (this.selectedZAxisKey && this.selectedZAxisKey.length > 0);
      

      
      return (
        isBasicFieldsFilled && isXAxisFilled && isYAxisFilled && isZAxisFilled
      );
    },
    shouldRenderChartList() {
      return this.shouldRenderComponent([
        "FluidsDataStudioTwo"
      ]);
    },
  },
  watch: {
    chartTitle: {
      handler(newVal) {
        this.internalChartTitle = newVal || '';
      },
      immediate: true,
    },
    selectedChartTypeName: "updateIsUpdateEnabled",
    selectedTableTypeName: "updateIsUpdateEnabled",
    selectedXAxisKey: "updateIsUpdateEnabled",
    selectedZAxisKey: "updateIsUpdateEnabled",
    xBins: "updateIsUpdateEnabled",
    yBins: "updateIsUpdateEnabled",
    xAxisScale: "updateIsUpdateEnabled",
    yAxisScale: "updateIsUpdateEnabled",
    accessLevel: "updateIsUpdateEnabled",
    selectedColorGroupKey(newValue) {
      this.updateIsUpdateEnabled();
      if (newValue === "") {
        this.selectedColorGroupKey = null;
      }
    },
    rightContentStyle(newStyle) {
      this.$emit("update-right-content-style", newStyle);
    },
    editPlotData: {
      handler(newVal, oldVal) {
        
        if (newVal && this.isEditing && !this.isPopulatingEditData) {
          this.populateEditData(newVal);
        } 
      },
      immediate: true,
      deep: true
    },
    isEditing(newVal, oldVal) {
      
      if (newVal && !oldVal && this.editPlotData && !this.isPopulatingEditData) {
        this.populateEditData(this.editPlotData);
      } else if (newVal && this.editPlotData && !this.isPopulatingEditData) {
        this.populateEditData(this.editPlotData);
      } else if (!newVal && oldVal) {
        this.isPopulatingEditData = false;
      } 
    },
    selectedChartType: {
      handler(newVal) {
        this.chartType = newVal || '';
      },
      immediate: true
    },
    chartTitle: {
      handler(newVal) {
        if (newVal !== undefined && newVal !== null) {
          this.internalChartTitle = newVal;
        }
      },
      immediate: true
    },
    xAxisItems: {
      handler(newVal, oldVal) {
        if (newVal && newVal.length > 0) {
          this.filteredXAxisItems = [...newVal];
          
          if (this.selectedXAxisKey && (this.isEditing || this.embedded) && (!oldVal || oldVal.length === 0)) {
            const item = newVal.find(item => item.name === this.selectedXAxisKey);
            if (item) {
              const currentValue = this.selectedXAxisKey;
              this.$nextTick(() => {
                if (this.selectedXAxisKey === currentValue) {
                  this.selectedXAxisKey = null;
                  this.$nextTick(() => {
                    this.selectedXAxisKey = currentValue;
                    this.$forceUpdate();
                  });
                }
              });
            }
          }
        }
      },
      deep: true
    },
    yAxisItems: {
      handler(newVal, oldVal) {
        if (newVal && newVal.length > 0) {
          this.filteredYAxisItems = [...newVal];
          
          if (this.selectedYAxisKey && Array.isArray(this.selectedYAxisKey) && this.selectedYAxisKey.length > 0 && (this.isEditing || this.embedded) && (!oldVal || oldVal.length === 0)) {
            const validKeys = this.selectedYAxisKey.filter(key => {
              const item = newVal.find(item => item.name === key);
              if (item && !this.filteredYAxisItems.find(f => f.name === key)) {
                this.filteredYAxisItems.push(item);
              }
              return !!item;
            });
            
            if (validKeys.length > 0) {
              const currentValues = [...this.selectedYAxisKey];
              this.$nextTick(() => {
                if (JSON.stringify(this.selectedYAxisKey) === JSON.stringify(currentValues)) {
                  this.selectedYAxisKey = [];
                  this.$nextTick(() => {
                    this.selectedYAxisKey = validKeys;
                    this.$forceUpdate();
                  });
                }
              });
            }
          }
        }
      },
      deep: true
    },
    zAxisItems: {
      handler(newVal, oldVal) {
        if (newVal && newVal.length > 0) {
          this.filteredZAxisItems = [...newVal];
          
          if (this.selectedZAxisKey && (this.isEditing || this.embedded) && (!oldVal || oldVal.length === 0)) {
            const item = newVal.find(item => item.name === this.selectedZAxisKey);
            if (item) {
              const currentValue = this.selectedZAxisKey;
              this.$nextTick(() => {
                if (this.selectedZAxisKey === currentValue) {
                  this.selectedZAxisKey = null;
                  this.$nextTick(() => {
                    this.selectedZAxisKey = currentValue;
                    this.$forceUpdate();
                  });
                }
              });
            }
          }
        }
      },
      deep: true
    },
    colorGroupItems: {
      handler(newVal, oldVal) {
        if (newVal && newVal.length > 0) {
          this.filteredColorGroupItems = [...newVal];
          
          if (this.selectedColorGroupKey && (this.isEditing || this.embedded) && (!oldVal || oldVal.length === 0)) {
            const item = newVal.find(item => item.name === this.selectedColorGroupKey);
            if (item) {
              const currentValue = this.selectedColorGroupKey;
              this.$nextTick(() => {
                if (this.selectedColorGroupKey === currentValue) {
                  this.selectedColorGroupKey = null;
                  this.$nextTick(() => {
                    this.selectedColorGroupKey = currentValue;
                    this.$forceUpdate();
                  });
                }
              });
            }
          }
        }
      },
      deep: true
    },
    isFormValid: {
      handler(newVal) {
        this.$emit('form-validation', newVal);
      },
      immediate: true
    },
    selectedYAxisKey: {
      handler(newVal, oldVal) {
        
        if (newVal && Array.isArray(newVal) && newVal.length > 0) {
          const filteredVal = this.filterLegendFromYAxis(newVal);
          if (filteredVal.length !== newVal.length) {
              this.selectedYAxisKey = filteredVal;
            return; // Don't call updateIsUpdateEnabled again
          }
        }
        
        this.updateIsUpdateEnabled();
      },
      deep: true
    },
  },
  methods: {
    cancelTableTypeRequest() {
      if (this.tableTypeAbortController) {
        this.tableTypeAbortController.abort();
        this.tableTypeAbortController = null;
      }
    },
    
    cancelChartTypeRequest() {
      if (this.chartTypeAbortController) {
        this.chartTypeAbortController.abort();
        this.chartTypeAbortController = null;
      }
    },
    
    cancelDataFetchRequest() {
      if (this.dataFetchAbortController) {
        this.dataFetchAbortController.abort();
        this.dataFetchAbortController = null;
      }
    },
    
    cancelChartUsageRequest() {
      if (this.chartUsageAbortController) {
        this.chartUsageAbortController.abort();
        this.chartUsageAbortController = null;
      }
    },
    
    cancelChartDataRequest() {
      if (this.chartDataAbortController) {
        this.chartDataAbortController.abort();
        this.chartDataAbortController = null;
      }
    },
    
    cancelAllRequests() {
      if (this.filterFetchTimer) {
        clearTimeout(this.filterFetchTimer);
        this.filterFetchTimer = null;
      }
      this.cancelTableTypeRequest();
      this.cancelChartTypeRequest();
      this.cancelDataFetchRequest();
      this.cancelChartUsageRequest();
      this.cancelChartDataRequest();
    },

    preserveYAxisData() {
      const filteredYAxisKeys = this.filterLegendFromYAxis(this.selectedYAxisKey);
      
      
      return {
        selectedYAxisKey: [...(filteredYAxisKeys || [])],
        yAxisData: this.yAxisData ? [...this.yAxisData] : []
      };
    },

    getStoredChartMetadata() {
      return getStoredChartData();
    },
    getResolvedFilterData(overrideFilter) {
      if (overrideFilter !== undefined && overrideFilter !== null) {
        return overrideFilter;
      }
      if (this.savedFilterData && Object.keys(this.savedFilterData).length > 0) {
        return this.savedFilterData;
      }
      if (this.editPlotData?.Filter && Object.keys(this.editPlotData.Filter).length > 0) {
        return this.editPlotData.Filter;
      }
      if (this.filterFromChart && Object.keys(this.filterFromChart).length > 0) {
        return this.filterFromChart;
      }
      const stored = this.getStoredChartMetadata();
      if (stored?.Filter && Object.keys(stored.Filter).length > 0) {
        return stored.Filter;
      }
      return overrideFilter || {};
    },
    applyFilterToQueryParams(filterData, queryParams) {
      if (!filterData) return;
      const minMaxFormatter = ({ min = "", max = "" }) => JSON.stringify({ min, max });
      if (filterData.asset?.length) queryParams.asset = JSON.stringify(filterData.asset);
      if (filterData.field?.length) queryParams.field = JSON.stringify(filterData.field);
      if (filterData.well?.length) queryParams.well = JSON.stringify(filterData.well);
      if (filterData.reservoir?.length) queryParams.reservoir = JSON.stringify(filterData.reservoir);
      if (filterData.fluidType?.length) queryParams.fluidType = JSON.stringify(filterData.fluidType);
      if (filterData.sampleType?.length) queryParams.sampleType = JSON.stringify(filterData.sampleType);
      if (filterData.fluidClassification?.length) {
        queryParams.fluidClassification = JSON.stringify(filterData.fluidClassification);
      }
      if (filterData.fluidSampleID?.length) {
        queryParams.fluidSampleID = JSON.stringify(filterData.fluidSampleID);
      }
      if (filterData.fluidSampleContainerID?.length) {
        queryParams.fluidSampleContainerID = JSON.stringify(filterData.fluidSampleContainerID);
      }
      if (filterData.source?.trim?.()) {
        queryParams.source = `"${filterData.source.trim()}"`;
      }
      if (filterData.sampleDate && this.hasValidMinMax(filterData.sampleDate)) {
        queryParams.sampleDate = minMaxFormatter(filterData.sampleDate);
      }
      if (filterData.depth && this.hasValidMinMax(filterData.depth)) {
        queryParams.depth = minMaxFormatter(filterData.depth);
      }
      if (filterData.formationPressure && this.hasValidMinMax(filterData.formationPressure)) {
        queryParams.formationPressure = minMaxFormatter(filterData.formationPressure);
      }
      if (filterData.formationTemperature && this.hasValidMinMax(filterData.formationTemperature)) {
        queryParams.formationTemperature = minMaxFormatter(filterData.formationTemperature);
      }
    },
    seedEditSessionFromView(session) {
      if (!session) return;
      if (session.tableData?.length) {
        this.tableData = session.tableData;
        this.$emit("table-data", this.tableData);
      }
      if (session.xAxisData?.length) this.xAxisData = session.xAxisData;
      if (session.yAxisData?.length) this.yAxisData = session.yAxisData;
      if (session.zAxisData?.length) this.zAxisData = session.zAxisData;
      if (session.xAxisDisplayName) this.xAxisDisplayName = session.xAxisDisplayName;
      if (session.yAxisDisplayNames?.length) {
        this.yAxisDisplayNames = [...session.yAxisDisplayNames];
      }
      if (session.zAxisDisplayName) this.zAxisDisplayName = session.zAxisDisplayName;
      if (session.colorGroupDisplayName) {
        this.colorGroupDisplayName = session.colorGroupDisplayName;
      }
      if (session.filter && Object.keys(session.filter).length > 0) {
        this.savedFilterData = session.filter;
        this.filterFromChart = session.filter;
      }
      if (this.tableData?.length) {
        this.emitAxisDisplayNamesFromTableData();
      }
      this.$emit("sample-error", "");
    },
    async refreshAxisEmitsFromTableData() {
      this.$emit("sample-error", "");
      if (!this.tableData?.length) {
        return;
      }
      await this.extractAllAxisData();
    },
    shouldSkipAxisDataFetch() {
      return this.isPopulatingEditData;
    },
    onFilterDataChanged(payload) {
      if (this.filterFetchTimer) {
        clearTimeout(this.filterFetchTimer);
      }
      this.filterFetchTimer = setTimeout(() => {
        this.filterFetchTimer = null;
        this.getTableDataAgainstFilter({
          filterData: payload?.filterData,
          type: payload?.type,
          filterCalled: true,
        });
      }, 300);
    },
    async fetchPlotTableDataForAxisChange(signal) {
      this.onlyApiCall = true;
      const filterData = this.getResolvedFilterData();
      const type =
        localStorage.getItem("selectedTableType") ||
        this.savedType ||
        this.selectedTableTypeName;
      await this.getTableDataAgainstFilter({ filterData, type }, signal);
    },
    hasConfiguredAxes() {
      return Boolean(this.selectedXAxisKey && String(this.selectedXAxisKey).length);
    },
    shouldRefreshDataOnly(filterCalled) {
      return Boolean(
        filterCalled &&
          this.hasConfiguredAxes() &&
          (this.isEditing || this.axisSelected)
      );
    },
    getXAxisDisplayNameForEmit() {
      return resolveColumnDisplayName(
        this.selectedXAxisKey,
        this.xAxisItems,
        this.xAxisDisplayName
      );
    },
    getYAxisDisplayNamesForEmit() {
      return resolveYAxisDisplayNames(
        this.selectedYAxisKey,
        this.yAxisItems,
        this.yAxisDisplayNames
      );
    },
    getZAxisDisplayNameForEmit() {
      return resolveColumnDisplayName(
        this.selectedZAxisKey,
        this.zAxisItems,
        this.zAxisDisplayName
      );
    },
    getColorGroupDisplayNameForEmit() {
      return resolveColumnDisplayName(
        this.selectedColorGroupKey,
        this.colorGroupItems,
        this.colorGroupDisplayName
      );
    },
    emitAxisDisplayNamesFromTableData() {
      if (!this.tableData?.length) return;
      const allCaptions = this.tableData.map((row) => row.caption || "");

      if (this.selectedXAxisKey) {
        this.xAxisDisplayName = this.getXAxisDisplayNameForEmit();
        this.$emit("x-axis-selected", {
          data: this.xAxisData,
          displayName: this.xAxisDisplayName,
          caption: allCaptions,
        });
      }

      if (this.selectedYAxisKey?.length) {
        this.yAxisDisplayNames = this.getYAxisDisplayNamesForEmit();
        this.$emit("y-axis-selected", {
          data: this.yAxisData,
          displayNames: this.yAxisDisplayNames,
          caption: allCaptions,
        });
      }

      if (this.selectedZAxisKey) {
        this.zAxisDisplayName = this.getZAxisDisplayNameForEmit();
        this.$emit("z-axis-selected", {
          data: this.zAxisData,
          displayName: this.zAxisDisplayName,
        });
      }

      if (this.selectedColorGroupKey) {
        this.colorGroupDisplayName = this.getColorGroupDisplayNameForEmit();
        this.$emit("color-group-display-name", this.colorGroupDisplayName);
      }
    },
    restoreYAxisData(preservedData) {
      if (preservedData && preservedData.selectedYAxisKey) {
        this.selectedYAxisKey = preservedData.selectedYAxisKey;
        if (preservedData.yAxisData && preservedData.yAxisData.length > 0) {
          this.yAxisData = preservedData.yAxisData;
        }
      }
    },

    resetUserSelectionFlag() {
      this.isUserSelecting = false;
    },

    filterLegendFromYAxis(yAxisKeys) {
      if (!yAxisKeys || !Array.isArray(yAxisKeys)) {
        return yAxisKeys;
      }
      
      const filteredKeys = yAxisKeys.filter(key => {
        const isLegendProperty = this.colorGroupItems.some(item => item.name === key);
        if (isLegendProperty) {
          void 0;
        }
        return !isLegendProperty;
      });
      
      
      return filteredKeys;
    },

    handleRightDrawerStateChange(isOpen) {
      if (this.screenWidth >= 768) {
        this.rightDrawerOpen = isOpen;
        localStorage.setItem('sidebarState', isOpen ? 'true' : 'false');
        this.$forceUpdate();
      }
      
      this.$emit("update-right-content-style", this.rightContentStyle);
      
      this.$nextTick(() => {
        eventBus.emit("resizeChart");
        eventBus.emit("filterDrawerToggled");
      });
    },
    handleXScaleToggleChange() {
      this.xAxisScale = this.isXLog ? "log" : "linear";
      this.$emit("x-axis-scale", this.xAxisScale);
    },
    handleYScaleToggleChange() {
      this.yAxisScale = this.isYLog ? "log" : "linear";
      this.$emit("y-axis-scale", this.yAxisScale);
    },
    resetFilterChartList() {
      this.chartSearchQuery = "";
      this.filteredChartsNames = this.savedChartsNames;
    },
    resetTableTypeList() {
      this.tableTypeSearchQuery = "";
      this.filteredTableTypesNames = this.tableTypesNames.map(name => ({ title: name, value: name }));
    },
    resetChartTypeList() {
      this.chartTypeSearchQuery = "";
      this.filteredChartTypesNames = this.chartTypesNames.map(name => ({ title: name, value: name }));
    },
    resetXAxisList() {
      this.xAxisSearchQuery = "";
      this.filteredXAxisItems = this.xAxisItems;
    },
    resetYAxisList() {
      this.yAxisSearchQuery = "";
      this.filteredYAxisItems = this.yAxisItems;
    },
    resetZAxisList() {
      this.zAxisSearchQuery = "";
      this.filteredZAxisItems = this.zAxisItems;
    },
    resetColorGroupList() {
      this.colorGroupSearchQuery = "";
      this.filteredColorGroupItems = this.colorGroupItems;
    },
    filterChartList() {
      const query = this.chartSearchQuery.toLowerCase();
      this.filteredChartsNames = this.savedChartsNames.filter((chart) =>
        chart.toLowerCase().includes(query)
      );
    },
    filterTableTypeList() {
      const query = this.tableTypeSearchQuery.toLowerCase();
      const filtered = this.tableTypesNames.filter((chart) =>
        chart.toLowerCase().includes(query)
      );
      this.filteredTableTypesNames = filtered.map(name => ({ title: name, value: name }));
    },
    filterChartTypeList() {
      const query = this.chartTypeSearchQuery.toLowerCase();
      const filtered = this.chartTypesNames.filter((chart) =>
        chart.toLowerCase().includes(query)
      );
      this.filteredChartTypesNames = filtered.map(name => ({ title: name, value: name }));
    },
    filterXAxisList() {
      const query = this.xAxisSearchQuery.toLowerCase();
      this.filteredXAxisItems = this.xAxisItems.filter((chart) =>
        chart.displayName.toLowerCase().includes(query)
      );
    },
    filterYAxisList() {
      const query = this.yAxisSearchQuery.toLowerCase();
      this.filteredYAxisItems = this.yAxisItems.filter((chart) =>
        chart.displayName.toLowerCase().includes(query)
      );
    },
    filterZAxisList() {
      const query = this.zAxisSearchQuery.toLowerCase();
      this.filteredZAxisItems = this.zAxisItems.filter((chart) =>
        chart.displayName.toLowerCase().includes(query)
      );
    },
    filterColorGroupList() {
      const query = this.colorGroupSearchQuery.toLowerCase();
      this.filteredColorGroupItems = this.colorGroupItems.filter((chart) =>
        chart.displayName.toLowerCase().includes(query)
      );
    },
    updateIsUpdateEnabled() {
      this.isUpdateEnabled = this.checkIfUpdateEnabled();
    },
    handleUpdateClick() {
      this.isUpdateEnabled = this.checkIfUpdateEnabled(); // Recalculate on button click
      if (this.isUpdateEnabled) {
        this.showUpdateConfirmation(this.selectedChartId);
      }
    },
    checkIfUpdateEnabled() {
      let initialChartData = JSON.parse(localStorage.getItem("initialChartData"));

      if (!initialChartData) return false;

      if (!this.selectedChartTypeName || !this.selectedTableTypeName) {
        return false;
      }

      const hasBasicFieldsChanged =
        this.selectedChartTypeName !== initialChartData.ChartType ||
        this.selectedTableTypeName !== initialChartData.TableType;

      const hasXAxisChanged =
        this.showXAxis && this.selectedXAxisKey !== initialChartData.XAxisColumn;

      const hasYAxisChanged =
        this.showYAxis &&
        Array.isArray(this.selectedYAxisKey) &&
        this.selectedYAxisKey.join(",") !== initialChartData.YAxisColumn;

      const hasZAxisChanged =
        this.showZAxis && this.selectedZAxisKey !== initialChartData.ZAxisColumn;

      const hasColorGroupChanged =
        (this.selectedColorGroupKey || "") !== (initialChartData.GroupColumn || "");

      const hasXBinsChanged =
        (this.xBins || null) !== (initialChartData.NumXBins || null);

      const hasYBinsChanged =
        (this.yBins || null) !== (initialChartData.NumYBins || null);

      const hasXScaleChanged =
        (this.xAxisScale || "") !== (initialChartData.XAxisScale || "");

      const hasYScaleChanged =
        (this.yAxisScale || "") !== (initialChartData.YAxisScale || "");

      const hasAccessLevelChanged =
        this.accessLevel !== initialChartData.AccessLevel;

      const initialAssets = initialChartData.Filter?.asset || [];
      const savedAssets = this.savedFilterData.asset ? Array.from(this.savedFilterData.asset) : [];

      const hasAssetFilterChanged =
        initialAssets.length !== savedAssets.length ||
        !initialAssets.every((item) => savedAssets.includes(item));

      const initialFields = initialChartData.Filter?.field || [];
      const savedFields = this.savedFilterData.field ? Array.from(this.savedFilterData.field) : [];

      const hasFieldFilterChanged =
        initialFields.length !== savedFields.length ||
        !initialFields.every((item) => savedFields.includes(item));

      const initialSampleKinds = initialChartData.Filter?.fluidType || [];
      const savedSampleKinds = this.savedFilterData.fluidType ? Array.from(this.savedFilterData.fluidType) : [];

      const hasSampleKindFilterChanged =
        initialSampleKinds.length !== savedSampleKinds.length ||
        !initialSampleKinds.every((item) => savedSampleKinds.includes(item));

      const initialWells = initialChartData.Filter?.well || [];
      const savedWells = this.savedFilterData.well ? Array.from(this.savedFilterData.well) : [];

      const hasWellFilterChanged =
        initialWells.length !== savedWells.length ||
        !initialWells.every((item) => savedWells.includes(item));

      const initialReservoirs = initialChartData.Filter?.reservoir || [];
      const savedReservoirs = this.savedFilterData.reservoir ? Array.from(this.savedFilterData.reservoir) : [];

      const hasReservoirFilterChanged =
        initialReservoirs.length !== savedReservoirs.length ||
        !initialReservoirs.every((item) => savedReservoirs.includes(item));

      return (
        hasBasicFieldsChanged ||
        hasXAxisChanged ||
        hasYAxisChanged ||
        hasZAxisChanged ||
        hasColorGroupChanged ||
        hasXBinsChanged ||
        hasYBinsChanged ||
        hasXScaleChanged ||
        hasYScaleChanged ||
        hasAccessLevelChanged ||
        hasAssetFilterChanged ||
        hasFieldFilterChanged ||
        hasSampleKindFilterChanged ||
        hasWellFilterChanged ||
        hasReservoirFilterChanged
      );
    },
    async showDeleteConfirmation(chartID) {
      try {
        const response = await api.get(`/public/api/charts/${chartID}/usage`);
        const data = response.data.data;
        if (Array.isArray(data) && data.length > 0) {
          this.confirmDeleteText = `This plot is used in ${data.length} dashboards. After deletion, it will be removed from all dashboards. Do you still want to delete this plot?`;
          this.confirmDeleteDialog = true;
        } else {
          this.confirmDeleteText = "Are you sure you want to delete plot?";
          this.confirmDeleteDialog = true;
        }
      } catch (error) {
        void 0;
      }
    },
    closeDeleteConfirmation() {
      this.confirmDeleteDialog = false;
    },
    async showUpdateConfirmation(chartID) {
      try {
        const response = await api.get(`/public/api/charts/${chartID}/usage`);
        const data = response.data?.data ?? [];

        if (Array.isArray(data) && data.length > 0) {
          this.confirmUpdateText = `This plot is used in ${data.length} dashboards. Do you still want to update this plot? If you update this plot, this change will be reflected in all dashboards as well.`;
        } else {
          this.confirmUpdateText = "Are you sure you want to update plot?";
        }

        this.confirmUpdateDialog = true;

      } catch (error) {
        void 0;
      }
    },

    closeUpdateConfirmation() {
      this.confirmUpdateDialog = false;
    },
    openDialog() {
      if (this.$refs.childComponent) {
        this.$refs.childComponent.openDialog();
      } else {
        void 0;
      }
    },
    toggleInputs(drawer) {
      this.$emit("handle-chart-height", drawer);
      
      this.$nextTick(() => {
        eventBus.emit("resizeChart");
        eventBus.emit("filterDrawerToggled");
      });
    },
    handleDrawerStateChange(isOpen) {
      if (this.screenWidth >= 768) {
        this.drawerOpen = isOpen;
        
        this.$nextTick(() => {
          eventBus.emit("resizeChart");
          eventBus.emit("filterDrawerToggled");
        });
      }
    },
    resetCallDefaultFilter() {
      this.callDefaultFilter = false;
    },
    callUpdateChartMethod() {
      this.updateChart()
    },
    handleResetSavedFilter() {
      this.filterReset = true;
      const savedChartData = localStorage.getItem("savedChartData") || localStorage.getItem("newsavedChartData");
      if (savedChartData) {
        const chartData = JSON.parse(savedChartData);
        if (chartData) {
          this.savedChartChangeLoad(chartData);
        }
      }
    },
    shouldRenderComponent(allowedRoutes) {
      return allowedRoutes.includes(this.$route.name);
    },
    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },
    goToCreateChart() {
      this.resetChartData();
    },
    async tableTypeChange(selectedType) {
      
      this.cancelTableTypeRequest();
      
      this.tableTypeAbortController = new AbortController();
      
      this.loadingXAxisData = true;
      this.loadingYAxisData = true;
      this.loadingZAxisData = true;
      
      const tableTypeValue = selectedType;
      this.selectedTableTypeName = selectedType;
      const tableTypeString = JSON.stringify(tableTypeValue);
      localStorage.setItem("selectedTableType", tableTypeValue);
      
      try {
        const response = await api.get(
          "public/api/tables/type?table=" +
          encodeURIComponent(tableTypeString),
          { signal: this.tableTypeAbortController.signal }
        );
        
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.loadingXAxisData = false;
          this.loadingYAxisData = false;
          this.loadingZAxisData = false;
          
          const columns = response.data.data.tableTypes.columns.filter(column => column.displayName);
          columns.sort((a, b) => (a.columnSortOrder || 999) - (b.columnSortOrder || 999));
          
          this.xAxisItems = columns.filter(column => column.canChart)
            .map(column => ({ displayName: column.displayName, name: column.name, sortOrder: column.sortOrder || 999 }))
            .sort((a, b) => a.sortOrder - b.sortOrder);
          this.filteredXAxisItems = [...this.xAxisItems];
          
          this.yAxisItems = columns.filter(column => column.canChart)
            .map(column => ({ displayName: column.displayName, name: column.name, sortOrder: column.sortOrder || 999 }))
            .sort((a, b) => a.sortOrder - b.sortOrder);
          this.filteredYAxisItems = [...this.yAxisItems];
          
          this.zAxisItems = columns.filter(column => column.canChart)
            .map(column => ({ displayName: column.displayName, name: column.name, sortOrder: column.sortOrder || 999 }))
            .sort((a, b) => a.sortOrder - b.sortOrder);
          this.filteredZAxisItems = [...this.zAxisItems];
          
          
          this.colorGroupItems = columns.filter(column => column.legend)
            .map(column => ({ displayName: column.displayName, name: column.name, sortOrder: column.legendSortOrder || 999 }))
            .sort((a, b) => a.sortOrder - b.sortOrder);
          this.filteredColorGroupItems = this.colorGroupItems;
          
          
          if (!this.isEditing) {
            this.resetAxisSelections();
          }
          this.saveChartData();
          
          let savedChartData = JSON.parse(localStorage.getItem("savedChartData"));
          if (savedChartData) {
            savedChartData.TableType = this.selectedTableTypeName;
            localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
          }
        } else if (response && response.data && response.data.message) {
          this.showSnackbar(response.data.message, "error");
        } else {
          this.showSnackbar("Error fetching table types.", "error");
        }
      } catch (error) {
        if (isRequestAborted(error)) {
          return;
        }
        
        this.loadingXAxisData = false;
        this.loadingYAxisData = false;
        this.loadingZAxisData = false;
      } finally {
        this.tableTypeAbortController = null;
      }
    },
    async tableTypeChangeAgainstFilter(selectedType) {
      this.cancelTableTypeRequest();
      
      this.tableTypeAbortController = new AbortController();
      
      this.loadingXAxisData = true;
      this.loadingYAxisData = true;
      this.loadingZAxisData = true;
      this.resetAxisSelections();
      this.selectedTableTypeName = selectedType;
      const tableTypeString = JSON.stringify(selectedType);
      localStorage.setItem("selectedTableType", this.selectedTableTypeName)
      try {
        const response = await api.get(
          "public/api/tables/type?table=" +
          encodeURIComponent(tableTypeString),
          { signal: this.tableTypeAbortController.signal }
        );
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.loadingXAxisData = false;
          this.loadingYAxisData = false;
          this.loadingZAxisData = false;
          const columns = response.data.data.tableTypes.columns.filter(column => column.displayName);
          columns.sort((a, b) => (a.columnSortOrder || 999) - (b.columnSortOrder || 999));
          this.xAxisItems = columns.filter(column => column.canChart)
            .map(column => ({ displayName: column.displayName, name: column.name, sortOrder: column.sortOrder || 999 })).sort((a, b) => a.sortOrder - b.sortOrder);
          this.filteredXAxisItems = this.xAxisItems
          this.yAxisItems = columns.filter(column => column.canChart)
            .map(column => ({ displayName: column.displayName, name: column.name, sortOrder: column.sortOrder || 999 })).sort((a, b) => a.sortOrder - b.sortOrder);
          this.filteredYAxisItems = this.yAxisItems
          this.zAxisItems = columns.filter(column => column.canChart)
            .map(column => ({ displayName: column.displayName, name: column.name, sortOrder: column.sortOrder || 999 })).sort((a, b) => a.sortOrder - b.sortOrder);
          this.filteredZAxisItems = this.zAxisItems
          this.colorGroupItems = columns.filter(column => column.legend)
            .map(column => ({ displayName: column.displayName, name: column.name, sortOrder: column.legendSortOrder || 999 })).sort((a, b) => a.sortOrder - b.sortOrder);
          this.filteredColorGroupItems = this.colorGroupItems
        }
        else if (response && response.data && response.data.message) {
          this.showSnackbar(response.data.message, "error");
        }
        else {
          this.showSnackbar("error fetching table types.", "error");
        }
      } catch (error) {
        if (isRequestAborted(error)) {
          return;
        }
        
      } finally {
        this.tableTypeAbortController = null;
      }
    },
    hasValidMinMax(obj) {
      if (!obj || typeof obj !== 'object') return false;
      const hasMin = obj.min !== null && obj.min !== undefined && obj.min !== '';
      const hasMax = obj.max !== null && obj.max !== undefined && obj.max !== '';
      return hasMin || hasMax;
    },
    async getTableData(signal) {
      
      const preservedYAxisData = this.preserveYAxisData();
      
      this.cancelDataFetchRequest();
      
      this.dataFetchAbortController = new AbortController();
      
      this.showLoaders = true;
      this.$emit("loading-state", this.showLoaders);
      this.$emit("sample-error", "");
      
      var selectedType = localStorage.getItem("selectedTableType");
      
      const selectedColumns = buildSelectColumnsFromState({
        xAxisKey: this.selectedXAxisKey,
        yAxisKeys: this.selectedYAxisKey,
        zAxisKey: this.selectedZAxisKey,
        groupColumn: this.selectedColorGroupKey,
      });
      
      if (selectedColumns.length === 0) {
        this.showLoaders = false;
        this.$emit("loading-state", this.showLoaders);
        return;
      }
      
      try {
        const queryParams = {
          selectColumns: JSON.stringify(selectedColumns),
        };

        const pendingFilterData = localStorage.getItem("pendingFilterData");
        const filterActive = localStorage.getItem("filterActive");
        let filterToApply = this.getResolvedFilterData();

        if (pendingFilterData && filterActive === "true") {
          try {
            const pendingFilter = JSON.parse(pendingFilterData);
            if (pendingFilter && Object.keys(pendingFilter).length > 0) {
              filterToApply = pendingFilter;
            }
          } catch (error) {
            console.error("Error parsing pending filter data in getTableData:", error);
          }
        }

        this.applyFilterToQueryParams(filterToApply, queryParams);
        
        const queryString = Object.entries(queryParams)
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join("&");


        const response = await api.get(
          `public/api/tables/data/${selectedType}?${queryString}`,
          { signal: this.dataFetchAbortController.signal }
        );
        
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          if (response.data.error) {
            this.$emit("sample-error", response.data.error);
            return;
          }
          
          this.tableData = parseTableSamples(response.data.data?.samples);
          
          this.$emit("table-data", this.tableData);

          if (this.tableData && this.tableData.length > 0) {
            const firstRow = this.tableData[0];
            selectedColumns.forEach(col => {
              const exists = col in firstRow;
              const value = firstRow[col];
              const valueType = typeof value;
            });
            
            const allKeys = Object.keys(firstRow);
            selectedColumns.forEach(col => {
              const variations = allKeys.filter(key => 
                key.toLowerCase().includes(col.toLowerCase()) || 
                col.toLowerCase().includes(key.toLowerCase())
              );
            });
          }
          
          if (this.tableData === null) {
            this.$emit("sample-error", "Data is not available to plot chart.");
            return;
          }
          
          await this.extractAllAxisData();
          
          this.resetUserSelectionFlag();
        } else if (response && response.data && response.data.message) {
          this.$emit("sample-error", response.data.message);
        } else if (response && response.data && response.data.error) {
          this.$emit("sample-error", response.data.error);
        } else {
          this.$emit("sample-error", "Error fetching table data.");
        }
      } catch (error) {
        if (isRequestAborted(error)) {
          this.restoreYAxisData(preservedYAxisData);
          return;
        }

        if (error.response?.data?.error) {
          this.$emit("sample-error", error.response.data.error);
        } else if (error.response?.data?.message) {
          this.$emit("sample-error", error.response.data.message);
        } else {
          this.$emit("sample-error", "An error occurred while fetching data.");
        }
      } finally {
        this.showLoaders = false;
        this.$emit("loading-state", this.showLoaders);
        this.dataFetchAbortController = null;
      }
    },
    async getTableDataAgainstFilter({ filterData, type, filterCalled }, signal) {
      if (filterCalled == true) {
        this.onlyApiCall = false;
      }

      const preservedYAxisData = this.preserveYAxisData();

      this.cancelDataFetchRequest();
      
      this.dataFetchAbortController = new AbortController();

      var selectedType = localStorage.getItem("selectedTableType") || type || this.selectedTableTypeName;
      const resolvedFilter = this.getResolvedFilterData(filterData);
      this.savedFilterData = resolvedFilter;
      this.savedType = selectedType;
      this.$emit("sample-error", "");
      this.showLoaders = true;
      this.$emit("loading-state", this.showLoaders);
      try {
        const queryParams = {};
        this.applyFilterToQueryParams(resolvedFilter, queryParams);

        const chartData = getStoredChartData();

        const selectedColumns = buildSelectColumnsFromState({
          xAxisKey: this.selectedXAxisKey,
          yAxisKeys: this.selectedYAxisKey,
          zAxisKey: this.selectedZAxisKey,
          groupColumn: this.selectedColorGroupKey,
          chartFallback: chartData,
        });

        if (chartData?.ChartType) {
          this.chartTypeChange(chartData.ChartType);
        }

        if (selectedColumns.length > 0) {
          queryParams.selectColumns = JSON.stringify(selectedColumns);
        } else {
          this.showLoaders = false;
          this.$emit("loading-state", this.showLoaders);
          return;
        }
        const queryString = Object.entries(queryParams)
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join("&");
        const response = await api.get(`public/api/tables/data/${selectedType}?${queryString}`,
          { signal: this.dataFetchAbortController.signal }
        );
        
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          if (response.data.error) {
            this.$emit("sample-error", response.data.error);
            return
          }
          
          this.xAxisData = [];
          this.zAxisData = [];
          this.colorGroupData = [];
          
          this.tableData = response.data.data.samples?.rows || [];
          this.$emit("table-data", this.tableData);
          const dataOnlyRefresh = this.shouldRefreshDataOnly(filterCalled);
          if (this.onlyApiCall === true || dataOnlyRefresh) {
            if (this.tableData?.length) {
              await this.extractAllAxisData();
            }
            this.onlyApiCall = false;
            return;
          }
          if (this.tableData === null) {
            this.$emit("sample-error", "Data is not available to plot chart.");
            return
          }
          const chartData = getStoredChartData();

          if (chartData) {
            this.internalChartTitle = chartData.Name;
            var chartTitle = this.internalChartTitle;
            this.$emit("chart-title", chartTitle);
            this.selectedChartTypeName = chartData.ChartType;
            this.selectedTableTypeName = chartData.TableType;
            if (!this.colorGroupChanged) {
              this.selectedColorGroupKey = chartData.GroupColumn;
            }
            
            if (chartData.XAxisColumn) {
              this.selectedXAxisKey = chartData.XAxisColumn;
            }
            if (chartData.ZAxisColumn) {
              this.selectedZAxisKey = chartData.ZAxisColumn;
            }
            
            this.xBins = 0;
            this.xBins = chartData.NumXBins;
            this.yBins = 0;
            this.yBins = chartData.NumYBins;
            this.$emit("update-x-bins", Number(this.xBins));
            this.$emit("update-y-bins", Number(this.yBins));
            
            if (chartData.XAxisScale) {
              this.xAxisScale = chartData.XAxisScale;
              this.isXLog = chartData.XAxisScale === "log";
              this.$emit("x-axis-scale", this.xAxisScale);
            }
            if (chartData.YAxisScale) {
              this.yAxisScale = chartData.YAxisScale;
              this.isYLog = chartData.YAxisScale === "log";
              this.$emit("y-axis-scale", this.yAxisScale);
            }
            
            const yKeysFromStoredChart = parseYAxisKeysFromChart(chartData);
            if (
              yKeysFromStoredChart.length > 0 &&
              (!this.selectedYAxisKey || this.selectedYAxisKey.length === 0) &&
              !this.isUserSelecting
            ) {
              this.selectedYAxisKey = this.filterLegendFromYAxis(yKeysFromStoredChart);
            }
            
            this.$nextTick(() => {
              this.$forceUpdate();
            });
          }
          var chartTitle = this.internalChartTitle
          this.$emit("chart-title", chartTitle);
          this.$nextTick(() => {
            if (this.selectedXAxisKey) {
              this.xAxisData = this.findXAxisData();
              var allCaptions = this.tableData?.map((row) => row.caption || ""); // Extract captions
              this.xAxisDisplayName = this.getXAxisDisplayNameForEmit();
              this.$emit("x-axis-selected", { data: this.xAxisData, displayName: this.xAxisDisplayName, caption: allCaptions });
            }
            else {
              this.colorGroupData = [];
              this.$emit("grouped-x-axis-data", { groupedXAxisData: [] });
            }
            if (this.selectedYAxisKey && this.selectedYAxisKey.length > 0) {
              
              this.yAxisData = this.findYAxisData();
              var allCaptions = this.tableData?.map((row) => row.caption || ""); // Extract captions
              
              this.yAxisDisplayNames = this.getYAxisDisplayNamesForEmit();
              
              this.$emit("y-axis-selected", { 
                data: this.yAxisData, 
                displayNames: this.yAxisDisplayNames, 
                caption: allCaptions 
              });
            }
            else {
               this.yAxisData = [];
              this.$emit("y-axis-selected", { data: [], displayNames: [] }); // Emit empty data
              this.colorGroupData = [];
              this.$emit("grouped-y-axis-data", { groupedYAxisData: [] });
              let savedChartData = JSON.parse(localStorage.getItem("savedChartData"));
              if (savedChartData) {
                savedChartData.YAxisColumn = "";
                localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
              }
              this.yAxisChanged = false;
            }
            if (this.selectedZAxisKey && this.selectedZAxisKey !== null && this.selectedZAxisKey !== undefined) {
              this.zAxisData = this.findZAxisData();
              this.zAxisDisplayName = this.getZAxisDisplayNameForEmit();
              this.$emit("z-axis-selected", { data: this.zAxisData, displayName: this.zAxisDisplayName });
            }
            else {
              this.colorGroupData = [];
              this.$emit("grouped-z-axis-data", { groupedZAxisData: [] });
              this.zAxisChanged = false;
            }
            
            this.processColorGroupDataForEditMode();
            
            this.resetUserSelectionFlag();
          });
        }
        else if (response && response.data && response.data.message) {
          this.$emit("sample-error", response.data.message);
        }
        else if (response && response.data && response.data.error) {
          this.$emit("sample-error", response.data.error);
        }
        else {
          this.$emit("sample-error", "Unexpected response structure.");
        }
      } catch (error) {
        if (isRequestAborted(error)) {
          this.restoreYAxisData(preservedYAxisData);
          return;
        }
        if (error.response?.data?.error) {
          this.$emit("sample-error", error.response.data.error);
        } else if (error.response?.data?.message) {
          this.$emit("sample-error", error.response.data.message);
        } else {
          this.$emit("sample-error", "An error occurred while fetching data.");
        }
      } finally {
        this.showLoaders = false;
        this.$emit("loading-state", this.showLoaders);
        this.dataFetchAbortController = null;
      }
    },
    chartTypeChange(selectedType) {
      this.chartType = selectedType;
      this.selectedChartTypeName = selectedType;
      
      this.$emit('chart-type-changed', selectedType);
      
      const selectedChart = this.chartTypes.find(
        (chart) => chart.Name === selectedType
      );
      
      if (selectedChart) {
        const originalSavedChartData = JSON.parse(localStorage.getItem("savedChartData"));
        let savedChartData = getStoredChartData() || originalSavedChartData;
        
        if (savedChartData && savedChartData?.Name !== "" && savedChartData?.Name !== null) {
          this.internalChartTitle = savedChartData.Name;
        }
        
        this.showXAxis = selectedChart.has_x;
        this.showYAxis = selectedChart.has_y;
        this.showZAxis = selectedChart.has_z;
        this.showColorGroup = selectedChart.has_group;
        this.showXBinsInput = selectedChart.has_x_bins;
        this.showYBinsInput = selectedChart.has_y_bins;
        
        if (this.showXBinsInput) {
          if (savedChartData && savedChartData?.NumXBins) {
            this.xBins = savedChartData.NumXBins;
          } else if (savedChartData) {
            if (this.chartType === "Histogram") {
              this.xBins = 10;
            } else {
              this.xBins = 20;
            }
              this.$emit("update-x-bins", Number(this.xBins));
            }
        } else {
          if (savedChartData && savedChartData?.NumXBins) {
            delete savedChartData.NumXBins;
            if (originalSavedChartData) {
              localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
            }
          }
        }
        
        if (this.showYBinsInput) {
          if (savedChartData?.NumYBins) {
            this.yBins = savedChartData.NumYBins;
          } else if (savedChartData) {
            this.yBins = 20;
            savedChartData.NumYBins = this.yBins;
            if (originalSavedChartData) {
              localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
            }
          }
        } else {
          if (savedChartData?.NumYBins) {
            delete savedChartData.NumYBins;
            if (originalSavedChartData) {
              localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
            }
          }
        }
        
        if (this.showColorGroup == true && savedChartData && savedChartData?.GroupColumn !== "" && savedChartData?.GroupColumn !== null && !this.colorGroupChanged) {
          this.selectedColorGroupKey = savedChartData.GroupColumn;
         }
        if (this.colorGroupChanged == true) {
          savedChartData.GroupColumn = this.selectedColorGroupKey;
        }
        if (this.showColorGroup == false) {
          this.selectedColorGroupKey = null;
          this.$emit("grouped-y-axis-data", { groupedYAxisData: [] });
          this.$emit("grouped-x-axis-data", { groupedXAxisData: [] });
          this.$emit("grouped-z-axis-data", { groupedZAxisData: [] });
        }
        
        this.selectedChartTypeName = selectedChart.Name;
        
        if (this.showXAxis == true && savedChartData && savedChartData?.XAxisColumn !== "" && savedChartData?.XAxisColumn !== null && this.xAxisChanged == false) {
          this.selectedXAxisKey = savedChartData.XAxisColumn;
        }
        if (this.xAxisChanged == true && this.selectedXAxisKey) {
          savedChartData.XAxisColumn = this.selectedXAxisKey;
        }
        
        var yAxisColumnArray = parseYAxisKeysFromChart(savedChartData || {});
        if (this.showYAxis == true && yAxisColumnArray.length > 0 && this.yAxisChanged == false) {
          if (originalSavedChartData) {
            this.selectedYAxisKey = yAxisColumnArray;
          }
        }
        if (this.yAxisChanged == true) {
          if (originalSavedChartData) {
            yAxisColumnArray = this.selectedYAxisKey;
          }
        }
        
        if (this.showYAxis == false && savedChartData.ChartType !== 'Histogram') {
          this.selectedYAxisKey = [];
          this.handleYAxisSelection();
          if (savedChartData) {
            savedChartData.YAxisColumn = this.selectedYAxisKey?.join(",");
            if (originalSavedChartData) {
              localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
            }
          }
        }
        
        if (this.showZAxis == true && savedChartData && savedChartData?.ZAxisColumn !== "" && savedChartData?.ZAxisColumn !== null && this.zAxisChanged == false) {
          this.selectedZAxisKey = savedChartData.ZAxisColumn;
        }
        if (this.zAxisChanged == true) {
          savedChartData.ZAxisColumn = this.selectedZAxisKey;
        }
        
        this.$emit("chart-type-changed", selectedType);
        this.saveChartData();
        
        if (savedChartData) {
          savedChartData.ChartType = selectedType;
          if (originalSavedChartData) {
            localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
          }
        }
        
      } else {
        console.error("Selected chart not found.");
      }
    },
    async savedChartChange() {
      this.showLoaders = true;
      this.$emit("loading-state", this.showLoaders);
      let defaultFilterID = localStorage.getItem("defaultFilter");
      const selectedChart = this.savedCharts.find(
        (chart) => chart.Name === this.selectedSavedChartName
      );
      if (selectedChart) {
        const chartId = selectedChart.ID;
        this.selectedChartId = selectedChart.ID;
        try {
          const response = await api.get(`public/api/charts/${chartId}`);
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            let selectedChartData = response.data.data.chart;
            const yKeys = parseYAxisKeysFromChart(selectedChartData);
            selectedChartData.YAxisColumn = buildYAxisColumnString(yKeys, selectedChartData.GroupColumn);
            selectedChartData.YAxisColumns = buildYAxisColumnsArray(yKeys, selectedChartData.GroupColumn);
            this.accessLevel = selectedChartData.AccessLevel;
            this.isPublic = this.accessLevel === "public";
            this.isXLog = selectedChartData.XAxisScale === "log";
            this.xAxisScale = selectedChartData.XAxisScale || "linear";
            this.isYLog = selectedChartData.YAxisScale === "log";
            this.yAxisScale = selectedChartData.YAxisScale || "linear";
            await this.tableTypeChange(selectedChartData.TableType);
            localStorage.setItem("savedChartData", JSON.stringify(selectedChartData));
            localStorage.setItem("initialChartData", JSON.stringify(selectedChartData));
            localStorage.setItem("newsavedChartData", JSON.stringify(selectedChartData));
            this.filterFromChart = selectedChartData.Filter;
            if (this.filterFromChart) {
              if (this.filterFromChart.ID !== undefined) {
                localStorage.setItem("selectedFilterId", this.filterFromChart.ID)
              }
              return
            }
            if (selectedChart && defaultFilterID) {
              this.callDefaultFilter = true;
              return;
            }
            this.showLoaders = false;
            this.$emit("loading-state", this.showLoaders);
            this.$nextTick(() => {
              this.populateChartData(selectedChartData);
            });
            this.createNewChart = true;
          }
          else if (response && response.data && response.data.message) {
            this.showSnackbar(response.data.message, "error");
          }
          else {
            this.showSnackbar("error fetching chart data.", "error");
          }
        } catch (error) {
          void 0;
        }
      } else {
        console.error("Selected chart not found.");
      }
    },
    async savedChartChangeLoad(chartData) {
      this.$emit("sample-error", "");
      this.showLoaders = true;
      this.$emit("loading-state", this.showLoaders);
      const savedChartSelected = localStorage.getItem("savedChartData")
      if (savedChartSelected) {
        this.selectedSavedChartName = chartData.Name;
      }
      let selectedChartData = chartData;
      await this.tableTypeChange(selectedChartData.TableType);
      this.accessLevel = selectedChartData.AccessLevel
      this.isPublic = this.accessLevel === "public";
      this.isXLog = selectedChartData.XAxisScale === "log";
      this.xAxisScale = selectedChartData.XAxisScale || "linear";
      this.isYLog = selectedChartData.YAxisScale === "log";
      this.yAxisScale = selectedChartData.YAxisScale || "linear";
      let defaultFilterID = localStorage.getItem("defaultFilter");
      this.chartTypeChange(chartData?.ChartType)
      if (this.filterReset === false) {
        this.filterFromChart = chartData.Filter;
        if (this.filterFromChart) {
          if (this.filterFromChart.ID !== undefined) {
            localStorage.setItem("selectedFilterId", this.filterFromChart.ID)
          }
          return;
        }
      }
      else if (this.filterReset === true) {
        this.filterFromChart = null
      }
      if (defaultFilterID) {
        return;
      }
      this.showLoaders = false;
      this.$emit("loading-state", this.showLoaders);
      this.$nextTick(() => {
        this.populateChartData(selectedChartData);
      });
      this.createNewChart = true;
    },
    updateChartTitle() {
      var chartTitle = this.internalChartTitle;
      this.$emit("chart-title", chartTitle);
    },
    async populateChartData(chartData) {
      this.colorGroupChanged = false;
      this.xAxisChanged = false;
      this.yAxisChanged = false;
      this.zAxisChanged = false;
      
      this.chartTitle = chartData.Name;
      var chartTitle = this.internalChartTitle;
      this.$emit("chart-title", chartTitle);
      this.selectedChartTypeName = chartData.ChartType;
      this.selectedTableTypeName = chartData.TableType;
      this.selectedColorGroupKey = chartData.GroupColumn;
      
      if (chartData.XAxisColumn && this.xAxisItems && this.xAxisItems.length > 0) {
        const selectedXAxisItem = this.xAxisItems.find(
          (item) => item.name === chartData.XAxisColumn
        );
        this.selectedXAxisKey = selectedXAxisItem ? selectedXAxisItem.name : chartData.XAxisColumn;
      } else if (chartData.XAxisColumn) {
        this.selectedXAxisKey = chartData.XAxisColumn;
      }

      if (this.tableData && chartData.XAxisColumn) {
        var columnData = this.tableData.map(
          (row) => row[chartData.XAxisColumn]
        );
        var allCaptions = this.tableData?.map((row) => row.caption || ""); // Extract caption data
        this.xAxisData = columnData;
        this.selectedXAxisKey = chartData.XAxisColumn;
        this.xAxisDisplayName = this.getXAxisDisplayNameForEmit();
        this.$emit("x-axis-selected", { data: this.xAxisData, displayName: this.xAxisDisplayName, caption: allCaptions });
      }
      else {
        this.colorGroupData = [];
        this.$emit("grouped-x-axis-data", { groupedXAxisData: [] });
      }
      const yKeysFromChart = parseYAxisKeysFromChart(chartData);
      if (yKeysFromChart.length > 0) {
        if (!this.selectedYAxisKey || this.selectedYAxisKey.length === 0) {
          this.selectedYAxisKey = this.filterLegendFromYAxis(
            yKeysFromChart.map((yAxisColumnName) => {
              const trimmed = String(yAxisColumnName).trim();
              const selectedYAxisItem = this.yAxisItems.find(
                (item) => item.name === trimmed
              );
              return selectedYAxisItem ? selectedYAxisItem.name : trimmed;
            }).filter(Boolean)
          );
        }
        if (this.tableData && this.selectedYAxisKey && this.selectedYAxisKey.length > 0) {
          var columnData = this.selectedYAxisKey.map((col) => {
            return this.tableData.map((row) => row[col]);
          });
          this.yAxisData = columnData;
          var allCaptions = this.tableData?.map((row) => row.caption || ""); // Extract caption data
          this.$emit("y-axis-selected", { data: this.yAxisData, caption: allCaptions });
        }
      } else {
        if (!this.selectedYAxisKey || this.selectedYAxisKey.length === 0) {
          this.selectedYAxisKey = [];
        }
      }
      if (chartData.ZAxisColumn && this.zAxisItems && this.zAxisItems.length > 0) {
        const selectedZAxisItem = this.zAxisItems.find(
          (item) => item.name === chartData.ZAxisColumn
        );
        this.selectedZAxisKey = selectedZAxisItem ? selectedZAxisItem.name : chartData.ZAxisColumn;
      } else if (chartData.ZAxisColumn) {
        this.selectedZAxisKey = chartData.ZAxisColumn;
      }

      this.chartTypeChange(this.selectedChartTypeName);
      this.xBins = chartData.NumXBins || 20;
      this.yBins = chartData.NumYBins || 20;
      this.$emit("update-x-bins", Number(this.xBins));
      this.$emit("update-y-bins", Number(this.yBins));
      
      if (chartData.XAxisScale) {
        this.xAxisScale = chartData.XAxisScale;
        this.isXLog = chartData.XAxisScale === "log";
      }
      if (chartData.YAxisScale) {
        this.yAxisScale = chartData.YAxisScale;
        this.isYLog = chartData.YAxisScale === "log";
      }
      
      if (this.selectedColorGroupKey && this.selectedColorGroupKey.length > 0) {
        this.colorGroupData = this.findcolorGroupData();
        
        if (this.colorGroupData && this.colorGroupData.length > 0) {
          const groupedYAxisData = {};
          const groupedXAxisData = {};
          const groupedZAxisData = {};
          
          this.colorGroupData.forEach(value => {
            groupedYAxisData[value] = [];
            groupedXAxisData[value] = [];
            groupedZAxisData[value] = [];
          });
          
          this.tableData.forEach(row => {
            const colorGroupValue = row[this.selectedColorGroupKey];
            if (this.colorGroupData.includes(colorGroupValue)) {
              const yAxisRowData = this.selectedYAxisKey.map(key => {
                const decimalKey = `${key}_decimal`;
                if (row[decimalKey] !== undefined && row[decimalKey] !== null) {
                  const value = parseFloat(row[key]);
                  if (!isNaN(value)) {
                    const maxDecimals = Math.min(row[decimalKey], 6);
                    return value.toFixed(maxDecimals);
                  }
                }
                return row[key];
              });
              groupedYAxisData[colorGroupValue].push(yAxisRowData);
              
              const xAxisRowData = row[this.selectedXAxisKey];
              groupedXAxisData[colorGroupValue].push(xAxisRowData);
              
              if (this.selectedZAxisKey) {
                const zAxisRowData = row[this.selectedZAxisKey];
                groupedZAxisData[colorGroupValue].push(zAxisRowData);
              }
            }
          });
          
          this.$emit("grouped-y-axis-data", groupedYAxisData);
          this.$emit("grouped-x-axis-data", groupedXAxisData);
          this.$emit("grouped-z-axis-data", groupedZAxisData);
          this.$emit("color-group-key", this.selectedColorGroupKey);
          
          this.colorGroupDisplayName = this.getColorGroupDisplayNameForEmit();
          this.$emit("color-group-display-name", this.colorGroupDisplayName);
          
          if (this.selectedYAxisKey && this.selectedYAxisKey.length > 0) {
            this.yAxisDisplayNames = this.getYAxisDisplayNamesForEmit();
            var allCaptions = this.tableData?.map((row) => row.caption || "");
            this.$emit("y-axis-selected", { 
              data: this.yAxisData, 
              displayNames: this.yAxisDisplayNames, 
              caption: allCaptions 
            });
          }
          
        }
      }
      
      this.$nextTick(() => {
        if (this.xAxisScale) {
          this.$emit("x-axis-scale", this.xAxisScale);
        }
        if (this.yAxisScale) {
          this.$emit("y-axis-scale", this.yAxisScale);
        }
        
        this.$forceUpdate();
      });
      
      this.updateAxisSelections();
      
      this.processColorGroupDataForEditMode();
    },
    updateAxisSelections() {
      this.handleXAxisSelection();
      this.handleYAxisSelection();
      this.handleZAxisSelection();
    },
    findYAxisData() {
      if (!this.tableData || !this.tableData.length) {
        return [];
      }
      
      if (!this.selectedYAxisKey || !this.selectedYAxisKey.length) {
        return [];
      }
      
      const firstRow = this.tableData[0];
      
      this.selectedYAxisKey.forEach(col => {
        const exactMatch = col in firstRow;
        const caseInsensitiveMatch = Object.keys(firstRow).find(key => 
          key.toLowerCase() === col.toLowerCase()
        );
        const partialMatch = Object.keys(firstRow).find(key => 
          key.includes(col) || col.includes(key)
        );
        
      });
      var columnData = this.selectedYAxisKey.map((col, colIndex) => {
        
        let actualColumnName = col;
        if (!(col in firstRow)) {
          const caseInsensitiveMatch = Object.keys(firstRow).find(key => 
            key.toLowerCase() === col.toLowerCase()
          );
          if (caseInsensitiveMatch) {
            actualColumnName = caseInsensitiveMatch;
            } else {
            const partialMatch = Object.keys(firstRow).find(key => 
              key.includes(col) || col.includes(key)
            );
            if (partialMatch) {
              actualColumnName = partialMatch;
            } else {
          
              const similarKeys = Object.keys(firstRow).filter(key => 
                key.toLowerCase().includes(col.toLowerCase().replace(/[^a-zA-Z0-9]/g, '')) ||
                col.toLowerCase().replace(/[^a-zA-Z0-9]/g, '').includes(key.toLowerCase())
              );
              if (similarKeys.length > 0) {
                actualColumnName = similarKeys[0]; // Use the first similar key
              }
            }
          }
        }
        
        const decimalKey = `${actualColumnName}_decimal`;
        
        const rawData = this.tableData.map((row, rowIndex) => {
          const value = row[actualColumnName];
          const decimalValue = row[decimalKey];
          
          if (decimalValue !== undefined && decimalValue !== null) {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              const maxDecimals = Math.min(decimalValue, 6);
              const numericValue = parseFloat(numValue.toFixed(maxDecimals)); // Convert back to number
            
              return numericValue;
            }
          }
          return value;
        });
        
        
        return rawData;
      });
      return columnData;
    },
    findXAxisData() {
      
      if (!this.tableData || !this.tableData.length) {
        return [];
      }
      
      if (!this.selectedXAxisKey) {
        return [];
      }
      
      const firstRow = this.tableData[0];
      
      
      const exactMatch = this.selectedXAxisKey in firstRow;
      const caseInsensitiveMatch = Object.keys(firstRow).find(key => 
        key.toLowerCase() === this.selectedXAxisKey.toLowerCase()
      );
      const partialMatch = Object.keys(firstRow).find(key => 
        key.includes(this.selectedXAxisKey) || this.selectedXAxisKey.includes(key)
      );
      
      let actualColumnName = this.selectedXAxisKey;
      if (!(this.selectedXAxisKey in firstRow)) {
        if (caseInsensitiveMatch) {
          actualColumnName = caseInsensitiveMatch;
        } else if (partialMatch) {
          actualColumnName = partialMatch;
          } 
      }
      
      const decimalKey = `${actualColumnName}_decimal`;
      
      const rawData = this.tableData.map((row, index) => {
        const value = row[actualColumnName];
        const decimalValue = row[decimalKey];

        
        if (decimalValue !== undefined && decimalValue !== null) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            const maxDecimals = Math.min(decimalValue, 6);
            const formattedValue = numValue.toFixed(maxDecimals);
            return formattedValue;
          }
        }
        return value;
      });
      
      return rawData;
    },
    findZAxisData() {
      if (this.tableData && this.selectedZAxisKey) {
        const decimalKey = `${this.selectedZAxisKey}_decimal`;
        return this.tableData.map((row) => {
          if (row[decimalKey] !== undefined && row[decimalKey] !== null) {
            const value = parseFloat(row[this.selectedZAxisKey]);
            if (!isNaN(value)) {
              const maxDecimals = Math.min(row[decimalKey], 6);
              return parseFloat(value.toFixed(maxDecimals)); // Convert back to number
            }
          }
          return row[this.selectedZAxisKey];
        });
      }
      return [];
    },
    findcolorGroupData() {
      if (this.tableData && this.selectedColorGroupKey) {
        var columnData = this.tableData.map(row => row[this.selectedColorGroupKey]);
        const uniqueValues = [...new Set(columnData.filter(item => item !== undefined && item !== null))];
        return uniqueValues;
      }
      return [];
    },
    saveChartData() {
      const groupColumn = this.selectedColorGroupKey || "";
      const yAxisColumnString = buildYAxisColumnString(this.selectedYAxisKey, groupColumn);
      const chartData = {
        AccessLevel: this.accessLevel,
        ChartType: this.selectedChartTypeName || "",
        GroupColumn: groupColumn || null,
        Name: this.internalChartTitle || "", // Use the current chart title
        TableType: this.selectedTableTypeName || "",
        XAxisColumn: this.selectedXAxisKey || null,
        YAxisColumn: yAxisColumnString || null,
        YAxisColumns: buildYAxisColumnsArray(this.selectedYAxisKey, groupColumn),
        ZAxisColumn: this.selectedZAxisKey || null,
        ...(this.selectedChartTypeName === "Histogram" || this.selectedChartTypeName === "2D Histogram"
          ? {
            NumXBins: Number(this.xBins),
            NumYBins: Number(this.yBins),
          }
          : {}),
      };
      localStorage.setItem("newsavedChartData", JSON.stringify(chartData));
      if (this.isEditing || this.isCreating || localStorage.getItem("savedChartData")) {
        localStorage.setItem("savedChartData", JSON.stringify(chartData));
      }
    },
    handleXSelection() {
      this.xAxisChanged = true;
      this.handleXAxisSelection()
    },
    async handleXAxisSelection() {
      this.axisSelected = true;
      
      if (this.selectedXAxisKey && this.selectedXAxisKey.length > 0) {
        if (this.shouldSkipAxisDataFetch()) {
          await this.refreshAxisEmitsFromTableData();
          this.xAxisChanged = false;
          return;
        }
        if (this.yAxisChanged === false || this.isEditing) {
          if (this.xAxisAbortController) {
            this.xAxisAbortController.abort();
          }
          this.xAxisAbortController = new AbortController();
          const signal = this.xAxisAbortController.signal;
          this.saveChartData();
          await this.fetchPlotTableDataForAxisChange(signal);
        }
        
        await this.extractAllAxisData();
        
        let savedChartData = JSON.parse(localStorage.getItem("savedChartData"));
        if (savedChartData) {
          savedChartData.XAxisColumn = this.selectedXAxisKey;
          localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
        }
        this.xAxisChanged = false;
      } else {
        this.colorGroupData = [];
        this.$emit("grouped-x-axis-data", { groupedXAxisData: [] });
        this.xAxisChanged = false;
      }
    },
    handleYSelection() {
      this.yAxisChanged = true;
      this.isUserSelecting = true; // Set flag to indicate user is making selections
      this.handleYAxisSelection()
    },
    async handleYAxisSelection() {
      if (!Array.isArray(this.selectedYAxisKey)) {
        this.selectedYAxisKey = this.selectedYAxisKey ? [this.selectedYAxisKey] : [];
      }

      if (this.shouldSkipAxisDataFetch()) {
        await this.refreshAxisEmitsFromTableData();
        this.yAxisChanged = false;
        this.isUserSelecting = false;
        return;
      }

      if (!this.xAxisData || this.xAxisData.length === 0) {
        await this.handleXAxisSelection();
        return;
      }
      
      this.axisSelected = true;
      
      if (this.selectedYAxisKey.length > 0) {
        if (this.yAxisAbortController) {
          this.yAxisAbortController.abort();
        }

        this.yAxisAbortController = new AbortController();
        const signal = this.yAxisAbortController.signal;

        this.saveChartData();
        await this.fetchPlotTableDataForAxisChange(signal);
        
        if (this.selectedYAxisKey.length > 1) {
          
          this.selectedYAxisKey = this.filterLegendFromYAxis(this.selectedYAxisKey);
          
          let savedChartData = JSON.parse(localStorage.getItem("savedChartData"));
          if (savedChartData) {
            savedChartData.GroupColumn = this.selectedColorGroupKey;
            localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
          }
        }
        
        await this.extractAllAxisData();
        
        let savedChartData = JSON.parse(localStorage.getItem("savedChartData"));
        if (savedChartData) {
          savedChartData.YAxisColumn = this.selectedYAxisKey.join(",");
          localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
        }
        this.yAxisChanged = false;
      } else {
        this.yAxisData = [];
        this.$emit("y-axis-selected", { data: [], displayNames: [] }); // Emit empty data
        this.colorGroupData = [];
        this.$emit("grouped-y-axis-data", { groupedYAxisData: [] });
        let savedChartData = JSON.parse(localStorage.getItem("savedChartData"));
        if (savedChartData) {
          savedChartData.YAxisColumn = "";
          localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
        }
        this.yAxisChanged = false;
      }
    },
    handleZSelection() {
      this.zAxisChanged = true;
      this.handleZAxisSelection()
    },
    async handleZAxisSelection() {
      if (this.shouldSkipAxisDataFetch()) {
        await this.refreshAxisEmitsFromTableData();
        this.zAxisChanged = false;
        return;
      }
      if (!this.xAxisData || this.xAxisData.length === 0) {
        await this.handleXAxisSelection();
        return;
      }
      if (!this.yAxisData || this.yAxisData.length === 0) {
        await this.handleYAxisSelection();
        return;
      }
      this.axisSelected = true;
      if (this.selectedZAxisKey) {
        if (this.zAxisAbortController) {
          this.zAxisAbortController.abort();
        }
        this.zAxisAbortController = new AbortController();
        const signal = this.zAxisAbortController.signal;

        this.saveChartData();
        await this.fetchPlotTableDataForAxisChange(signal);
        
        await this.extractAllAxisData();
        
        let savedChartData = JSON.parse(localStorage.getItem("savedChartData"));
        if (savedChartData) {
          savedChartData.ZAxisColumn = this.selectedZAxisKey
          localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
        }
        this.zAxisChanged = false;
      }
      else {
        this.colorGroupData = [];
        this.$emit("grouped-z-axis-data", { groupedZAxisData: [] });
        this.zAxisChanged = false;
      }
    },
    handleColorGroupSelection() {
      this.axisSelected = true;
      this.colorGroupChanged = true;
      
      if (this.colorGroupSelectionTimeout) {
        clearTimeout(this.colorGroupSelectionTimeout);
      }
      
      this.colorGroupSelectionTimeout = setTimeout(() => {
        this.handleGroupSelection();
      }, 300); // 300ms debounce
    },
    async handleGroupSelection() {
      if (this.shouldSkipAxisDataFetch()) {
        await this.refreshAxisEmitsFromTableData();
        this.processColorGroupDataForEditMode();
        this.colorGroupChanged = false;
        return;
      }

      if (this.colorGroupAbortController) {
        this.colorGroupAbortController.abort();
      }
      this.colorGroupAbortController = new AbortController();
      const signal = this.colorGroupAbortController.signal;

      this.saveChartData();

      try {
        await this.fetchPlotTableDataForAxisChange(signal);
        await this.extractAllAxisData();
        this.$emit("color-group-key", this.selectedColorGroupKey || "");
      } catch (error) {
        if (isRequestAborted(error)) {
          return;
        }
        console.error('Error in color group API call:', error);
        this.$emit(
          "sample-error",
          this.selectedColorGroupKey
            ? "Error updating chart data for color grouping."
            : "Error updating chart data after removing color grouping."
        );
        return;
      } finally {
        this.colorGroupChanged = false;
      }
    },
    resetAxisSelections() {
      this.selectedXAxisKey = [];
      this.selectedYAxisKey = [];
      this.selectedZAxisKey = [];
      const containsSampleUID = this.colorGroupItems.some(
        item => item.displayName === "Sample UID"
      );
      if (containsSampleUID) {
        this.sampleIdPresent = true;
      } else {
        this.selectedColorGroupKey = null;
      }
      this.$emit("grouped-y-axis-data", { groupedYAxisData: [] });
      this.$emit("grouped-x-axis-data", { groupedXAxisData: [] });
      this.$emit("grouped-z-axis-data", { groupedZAxisData: [] });
      this.xAxisData = [];
      this.$emit("x-axis-selected", { data: this.xAxisData });
      this.yAxisData = [];
      this.$emit("y-axis-selected", { data: this.yAxisData });
      this.zAxisData = [];
      this.zAxisDisplayName = "";
      this.$emit("z-axis-selected", { data: this.zAxisData, displayName: this.zAxisDisplayName });
    },
    async getChartTypes() {
      this.loadingChartType = true;
      await api
        .get("public/api/charts/type")
        .then((response) => {
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            this.chartTypes = response.data.data.chart_types;
            this.chartTypes = response.data.data.chart_types;
            this.chartTypesNames = response.data.data.chart_types.map(
              (chart) => chart.Name
            );
            this.filteredChartTypesNames = this.chartTypesNames.map(name => ({ title: name, value: name }))
          }
          else if (response && response.data && response.data.message) {
            this.showSnackbar(response.data.message, "error");
          }
          else {
            this.showSnackbar("error fetching chart types.", "error");
          }
        })
        .catch((error) => {
          void 0;
        })
        .finally(() => {
          this.loadingChartType = false;
        });
    },
    getTableTypes() {
      this.showLoaders = true;
      this.loadingTableType = true;
      api
        .get("public/api/tables/type")
        .then((response) => {
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            this.tableTypes = response.data.data.tableTypes;
            this.tableTypes = response.data.data.tableTypes;
            this.tableTypesNames = response.data.data.tableTypes.map(
              (chart) => chart.table
            );
            this.filteredTableTypesNames = this.tableTypesNames.map(name => ({ title: name, value: name }))
          }
          else if (response && response.data && response.data.message) {
            this.showSnackbar(response.data.message, "error");
          }
          else {
            this.showSnackbar("error fetching table types.", "error");
          }
        })
        .catch((error) => {
          void 0;
        })
        .finally(() => {
          this.showLoaders = false;
          this.loadingTableType = false;
        });
    },
    handleToggleChange() {
      this.accessLevel = this.isPublic ? "public" : "private";
    },
    handleAccessLevelChange(newAccessLevel) {
      this.accessLevel = newAccessLevel;
      this.isPublic = newAccessLevel === "public";
    },
    async createChart(access) {
      
      this.createLoading = true;
      this.showLoaders = true;
      this.$emit("loading-state", this.showLoaders);
      
      const chartType = this.selectedChartTypeName || "";
      const tableType = this.selectedTableTypeName || "";
      const selectedChartType = this.chartTypes.find(
        (chart) => chart.Name === chartType
      );
      
      let YAxisColumns = this.showYAxis ? [...this.selectedYAxisKey] : [];

      if (this.showColorGroup && this.selectedColorGroupKey) {
        YAxisColumns.push(this.selectedColorGroupKey);
      }
      
      var Filters = {};
      try {
        await this.$nextTick();
        
        if (this.$refs.filters) {
          Filters = this.$refs.filters.saveFilterWithChart();
          
          if (Filters && Object.keys(Filters).length > 0) {
            void 0;
          } else {
            console.warn('Filters object is empty or contains no data - this is normal for new charts')
            Filters = {
              ID: null,
              name: '',
              asset: [],
              field: [],
              reservoir: [],
              well: [],
              sampleDate: null,
              fluidType: [],
              sampleType: [],
              fluidClassification: [],
              fluidSampleID: [],
              fluidSampleContainerID: [],
              source: '',
              user: this.userFullName || '',
              public: true
            };
          }
        } else {
          console.warn('Filters component reference not found - this.$refs.filters is undefined');
          Filters = {
            ID: null,
            name: '',
            asset: [],
            field: [],
            reservoir: [],
            well: [],
            sampleDate: null,
            fluidType: [],
            sampleType: [],
            fluidClassification: [],
            fluidSampleID: [],
            fluidSampleContainerID: [],
            source: '',
            user: this.userFullName || '',
            public: true
          };
        }
      } catch (error) {
        console.error('Error getting filters:', error);
        Filters = {
          ID: null,
          name: '',
          asset: [],
          field: [],
          reservoir: [],
          well: [],
          sampleDate: null,
          fluidType: [],
          sampleType: [],
          fluidClassification: [],
          fluidSampleID: [],
          fluidSampleContainerID: [],
          source: '',
          user: this.userFullName || '',
          public: true
        };
      }
      
      const requestData = {
        data: {
          chart: {
            name: this.internalChartTitle,
            ChartType: chartType,
            TableType: tableType,
            XAxisColumn: this.selectedXAxisKey,
            AccessLevel: access,
            Filter: Filters,
            XAxisScale: this.xAxisScale,
            YAxisScale: this.yAxisScale,
          },
        },
      };
      
      if (this.showYAxis && YAxisColumns.length > 0) {
        requestData.data.chart.YAxisColumns = YAxisColumns;
      }
      if (this.showZAxis && this.selectedZAxisKey) {
        requestData.data.chart.ZAxisColumn = this.selectedZAxisKey;
      }
      if (selectedChartType && selectedChartType.has_x_bins) {
        requestData.data.chart.NumXBins = this.xBinsAsNumber;
      }
      if (selectedChartType && selectedChartType.has_y_bins) {
        requestData.data.chart.NumYBins = this.yBinsAsNumber;
      }
      if (this.showColorGroup && this.selectedColorGroupKey) {
        requestData.data.chart.GroupColumn = this.selectedColorGroupKey;
      }
      
      
      api
        .post("public/api/charts", requestData)
        .then((response) => {
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            this.createLoading = false;
            this.showLoaders = false;
            this.$emit("loading-state", this.showLoaders);
            this.accessLevel = requestData.data.chart.AccessLevel;
            this.showSnackbar(response.data.message || "Chart created successfully!", "success");
            
            if (this.$refs.filters) {
              this.$refs.filters.clearPendingFilters();
            }
            
            this.getSavedCharts();
            this.$emit("chart-saved-studio", requestData.data.chart);
            this.createNewChart = true;
            this.selectedSavedChartName = requestData.data.chart.name;
            let chartId = response.data.data.ID;
            this.getChartById(chartId);
          } else if (response && response.data && response.data.message) {
            this.showSnackbar(response.data.message, "error");
            this.createLoading = false;
            this.showLoaders = false;
            this.$emit("loading-state", this.showLoaders);
          } else {
            this.showSnackbar("Error creating chart.", "error");
            this.createLoading = false;
            this.showLoaders = false;
            this.$emit("loading-state", this.showLoaders);
          }
          localStorage.removeItem("newsavedChartData");
        })
        .catch((error) => {
          console.error('Error creating chart:', error);
          this.createLoading = false;
          this.showLoaders = false;
          this.$emit("loading-state", this.showLoaders);
          if (error.response && error.response.data && error.response.data.error) {
          this.showSnackbar(error.response.data.error, "error");
          } else {
            this.showSnackbar("An error occurred while creating the chart.", "error");
          }
        });
    },
    async deleteChart() {
      const selectedChart = this.savedCharts.find(
        (chart) => chart.Name === this.selectedSavedChartName
      );
      if (!selectedChart) {
        console.error("Selected chart not found.");
        return;
      }
      this.deleteLoading = true;
      const chartId = selectedChart.ID;
      try {
        const response = await api.delete(`public/api/charts/${chartId}`);

        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.deleteLoading = false;
          this.showSnackbar(response.data.message, "success");
          this.getSavedCharts();
          this.resetChartData();
          this.chartSaved = true;
          this.$emit("chart-saved", this.chartSaved);
          this.confirmDeleteDialog = false;
        }
        else if (response && response.data && response.data.statusCode === 500) {
          this.deleteLoading = false;
          this.showSnackbar(response.data.error, "error");
        }
        else if (response && response.data && response.data.message) {
          this.deleteLoading = false;
          this.showSnackbar(response.data.message, "error");
        }
        else {
          this.deleteLoading = false;
          this.showSnackbar("error deleting chart.", "error");
        }
      } catch (error) {
        this.deleteLoading = false;
        if (error.response && error.response.data && error.response.data.error) {
          this.showSnackbar(error.response.data.error, "error");
        } else if (error.response && error.response.data && error.response.data.message) {
          this.showSnackbar(error.response.data.message, "error");
        } else {
          this.showSnackbar("An error occurred while deleting the chart.", "error");
        }
      }
    },
    handleSaveInEditMode() {
      
      if (this.editPlotData && this.editPlotData.ID) {
        if (!this.selectedSavedChartName && this.editPlotData.Name) {
          this.selectedSavedChartName = this.editPlotData.Name;
        }
      }
      
      if (!this.selectedSavedChartName && this.editPlotData && this.editPlotData.Name) {
        this.selectedSavedChartName = this.editPlotData.Name;
      }
      
      this.updateChart();
    },
    async updateChart(accessLevel = null) {
      
  if (accessLevel !== null) {
    this.accessLevel = accessLevel;
  }
      
      let selectedChart = null;
      if (this.selectedSavedChartName) {
        selectedChart = this.savedCharts.find(
          (chart) => chart.Name === this.selectedSavedChartName
        );
      }
      
      if (!selectedChart && this.editPlotData && this.editPlotData.ID) {
        selectedChart = {
          ID: this.editPlotData.ID,
          Name: this.editPlotData.Name || this.editPlotData.chartName || this.editPlotData.plotName
        };
        if (!this.selectedSavedChartName && selectedChart.Name) {
          this.selectedSavedChartName = selectedChart.Name;
        }
      }
      
      if (!selectedChart) {
        console.error("Selected chart not found.", {
          selectedSavedChartName: this.selectedSavedChartName,
          hasEditPlotData: !!this.editPlotData,
          editPlotDataId: this.editPlotData?.ID,
          savedChartsLength: this.savedCharts?.length
        });
        this.showSnackbar("Chart not found. Please try again.", "error");
        return;
      }
      this.updateLoading = true;
      const chartId = selectedChart.ID;
      const chartType = this.selectedChartTypeName
        ? this.selectedChartTypeName
        : "";
      const tableType = this.selectedTableTypeName
        ? this.selectedTableTypeName
        : "";
      const selectedChartType = this.chartTypes.find(
        (chart) => chart.Name === chartType
      );
      let YAxisColumns = this.showYAxis ? [...this.selectedYAxisKey] : [];
      if (this.showColorGroup && this.selectedColorGroupKey) {
        YAxisColumns.push(this.selectedColorGroupKey);
      }
      let Filters = {};
      if (this.$refs.filters && typeof this.$refs.filters.saveFilterWithChart === 'function') {
        Filters = this.$refs.filters.saveFilterWithChart();
      } else {
        Filters = this.editPlotData?.Filter || {};
      }
      const requestData = {
        data: {
          chart: {
            ID: chartId,
            name: this.internalChartTitle,
            ChartType: chartType,
            TableType: tableType,
            XAxisColumn: this.selectedXAxisKey,
            AccessLevel: this.accessLevel,
            Filter: Filters,
            XAxisScale: this.xAxisScale,
            YAxisScale: this.yAxisScale,
          },
        },
      };
      if (this.showYAxis) {
        requestData.data.chart.YAxisColumns = YAxisColumns;
      }
      if (this.showZAxis) {
        requestData.data.chart.ZAxisColumn = this.selectedZAxisKey;
      }
      if (selectedChartType && selectedChartType.has_x_bins) {
        requestData.data.chart.NumXBins = this.xBinsAsNumber;
      }
      if (selectedChartType && selectedChartType.has_y_bins) {
        requestData.data.chart.NumYBins = this.yBinsAsNumber;
      }
      if (this.showColorGroup && this.selectedColorGroupKey) {
        requestData.data.chart.GroupColumn = this.selectedColorGroupKey
      }
      if (this.showColorGroup && !this.selectedColorGroupKey) {
        requestData.data.chart.GroupColumn = ""
      }
        
      
      try {
        const response = await api.put(
          `public/api/charts/${chartId}`,
          requestData
        );
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.updateLoading = false;
          this.accessLevel = requestData.data.chart.AccessLevel;
          this.showSnackbar(response.data.message, "success");
          
          if (this.$refs.filters && typeof this.$refs.filters.clearPendingFilters === 'function') {
            this.$refs.filters.clearPendingFilters();
          }
          
          const yAxisColumnString = buildYAxisColumnString(
            this.selectedYAxisKey,
            this.selectedColorGroupKey || ""
          );
          const updatedInitialChartData = {
            ChartType: chartType,
            TableType: tableType,
            XAxisColumn: this.selectedXAxisKey,
            YAxisColumn: yAxisColumnString,
            YAxisColumns: YAxisColumns,
            ZAxisColumn: this.selectedZAxisKey,
            GroupColumn: this.selectedColorGroupKey || '',
            NumXBins: requestData.data.chart.NumXBins || null,
            NumYBins: requestData.data.chart.NumYBins || null,
            XAxisScale: this.xAxisScale,
            YAxisScale: this.yAxisScale,
            AccessLevel: this.accessLevel,
            Filter: Filters
          };
          localStorage.setItem("initialChartData", JSON.stringify(updatedInitialChartData));
          localStorage.setItem("savedChartData", JSON.stringify({
            ...updatedInitialChartData,
            ID: chartId,
            Name: this.internalChartTitle,
          }));
          localStorage.setItem("newsavedChartData", JSON.stringify({
            ...updatedInitialChartData,
            ID: chartId,
            Name: this.internalChartTitle,
          }));

          this.isUpdateEnabled = this.checkIfUpdateEnabled();

          const apiChart = response.data.data?.chart || response.data.data;
          const mergedChart = mergeChartMetadataAfterUpdate(
            updatedInitialChartData,
            apiChart,
            {
              ...requestData.data.chart,
              name: this.internalChartTitle,
              YAxisColumns: YAxisColumns,
              YAxisColumn: yAxisColumnString,
              Filter: Filters,
            }
          );
          mergedChart.ID = chartId;
          mergedChart.Name = this.internalChartTitle;
          localStorage.setItem("savedChartData", JSON.stringify(mergedChart));
          localStorage.setItem("newsavedChartData", JSON.stringify(mergedChart));

          await this.reloadPlotTableDataAfterUpdate(Filters);

          this.getSavedCharts();
          this.chartSaved = true;
          var chartSaved = this.chartSaved;
          this.$emit("chart-saved", chartSaved, requestData);
          this.$emit("chart-updated");
          this.$emit("chart-fetched", mergedChart);
          this.getChartById(chartId);
          this.confirmUpdateDialog = false;
        }
        else if (response && response.data && response.data.message) {
          this.showSnackbar(response.data.message, "error");
          this.updateLoading = false;
        }
        else {
          this.updateLoading = false;
          console.error('API response error - unexpected status:', response);
          this.showSnackbar("An error occurred while updating the chart.", "error");
        }
      } catch (error) {
        console.error('Error in updateChart API call:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          request: error.config
        });
        this.updateLoading = false;
        if (error.response && error.response.data && error.response.data.error) {
          this.showSnackbar(error.response.data.error, "error");
        } else if (error.response && error.response.data && error.response.data.message) {
          this.showSnackbar(error.response.data.message, "error");
        } else {
          this.showSnackbar("An error occurred while updating the chart.", "error");
        }
        throw error;
      }
    },
    async reloadPlotTableDataAfterUpdate(filterData) {
      this.onlyApiCall = false;
      const type = localStorage.getItem("selectedTableType") || this.selectedTableTypeName;

      try {
        const resolved =
          filterData !== undefined && filterData !== null
            ? filterData
            : this.getResolvedFilterData();
        await this.getTableDataAgainstFilter({
          filterData: resolved,
          type,
          filterCalled: true,
        });

        if (this.tableData?.length) {
          await this.extractAllAxisData();
        }
      } catch (error) {
        console.error("Error reloading plot table data:", error);
      }
    },
    async getChartById(chartId) {
      this.selectedChartId = chartId;
      try {
        const response = await api.get(
          `public/api/charts/${chartId}`
        );
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          let chartDataById = response.data.data.chart;
          const yKeys = parseYAxisKeysFromChart(chartDataById);
          chartDataById.YAxisColumn = buildYAxisColumnString(yKeys, chartDataById.GroupColumn);
          chartDataById.YAxisColumns = buildYAxisColumnsArray(yKeys, chartDataById.GroupColumn);

          this.selectedSavedChartName = chartDataById.Name
          localStorage.setItem("savedChartData", JSON.stringify(chartDataById));
          localStorage.setItem("initialChartData", JSON.stringify(chartDataById));
          localStorage.setItem("newsavedChartData", JSON.stringify(chartDataById));
          this.isUpdateEnabled = false;
          if (chartDataById.Filter && chartDataById.Filter.ID !== undefined) {
            localStorage.setItem("selectedFilterId", chartDataById.Filter.ID)
          }
          
          this.$emit("chart-fetched", chartDataById);
        }
        else if (response && response.data && response.data.message) {
          this.showSnackbar(response.data.message, "error");
        }
        else {
          this.showSnackbar("error fetching chart data.", "error");
        }
      } catch (error) {
        console.log(error)
      }
    },
    resetChartData() {
      this.showLoaders = true;
      this.internalChartTitle = "";
      this.resetAxisSelections();
      this.$emit("chart-type-changed", "");
      this.$emit("chart-title", "");
      this.showXBinsInput = false;
      this.showYBinsInput = false;
      this.showZAxis = false;
      this.showColorGroup = false;
      this.selectedTableTypeName = null;
      this.selectedChartTypeName = null;
      this.selectedXAxisKey = [];
      this.selectedYAxisKey = [];
      this.selectedZAxisKey = [];
      this.selectedSavedChartName = null;
      this.showLoaders = false;
      this.createNewChart = false;
      this.accessLevel = "private",
        this.isPublic = this.accessLevel === "public";
      this.xAxisScale = "linear";
      this.yAxisScale = "linear";
      this.isXLog = false;
      this.isYLog = false;
      this.$emit("x-axis-scale", this.xAxisScale);
      this.$emit("y-axis-scale", this.yAxisScale);
      localStorage.removeItem("savedChartData");
      localStorage.removeItem("initialChartData");
      localStorage.removeItem("selectedFilterId");
      this.showInputs = true;
      this.$refs.filters.resetFields();
      
      this.tableData = [];
      this.colorGroupChanged = false;
      this.xAxisChanged = false;
      this.yAxisChanged = false;
      this.zAxisChanged = false;
      this.axisSelected = false;
      this.sampleIdPresent = false;
      this.onlyApiCall = false;
      
      this.xAxisDisplayName = "";
      this.yAxisDisplayNames = [];
      this.zAxisDisplayName = "";
      this.xAxisCaption = [];
      this.yAxisCaption = [];
    },
    getSavedCharts() {
      this.showLoaders = true;
      this.loadingSavedCharts = true;
      api
        .get("public/api/charts")
        .then((response) => {
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            if (response.data.data.charts) {
              this.savedCharts = response.data.data.charts;
              this.savedChartsNames = response.data.data.charts.map(
                (chart) => chart.Name
              );
              this.filteredChartsNames = this.savedChartsNames;
            } else {
              this.resetChartData();
            }
          }
          else if (response && response.data && response.data.message) {
            this.showSnackbar(response.data.message, "error");
          }
          else {
            this.showSnackbar("error fetching charts.", "error");
          }
        })
        .catch((error) => {
          void 0;
        })
        .finally(() => {
          this.showLoaders = false;
          this.loadingSavedCharts = false;
        });
    },
    async extractAllAxisData() {
      
      
      if (!this.tableData || this.tableData.length === 0) {
        return;
      }
      
      this.showLoaders = true;
      this.$emit("loading-state", this.showLoaders);
      
      this.xAxisData = [];
      this.yAxisData = [];
      this.zAxisData = [];
      this.colorGroupData = [];
      
      this.autoFixColumnNames();
      
      const isLargeDataset = this.tableData.length > this.dataChunkSize;
      
      try {
      if (this.selectedXAxisKey) {
        if (isLargeDataset) {
          this.xAxisData = await this.findXAxisDataChunked();
        } else {
          this.xAxisData = this.findXAxisData();
        }
        
        var allCaptions = this.tableData?.map((row) => row.caption || ""); // Extract caption data
        this.xAxisDisplayName = this.getXAxisDisplayNameForEmit();
        this.$emit("x-axis-selected", { 
          data: this.xAxisData, 
          displayName: this.xAxisDisplayName, 
          caption: allCaptions 
        });
        } 
      
      if (this.selectedYAxisKey && this.selectedYAxisKey.length > 0) {
          
        if (isLargeDataset) {
          this.yAxisData = await this.findYAxisDataChunked();
        } else {
          this.yAxisData = this.findYAxisData();
        }
        
        var allCaptions = this.tableData?.map((row) => row.caption || ""); // Extract caption data
        this.yAxisDisplayNames = this.getYAxisDisplayNamesForEmit();
        this.$emit("y-axis-selected", { 
          data: this.yAxisData, 
          displayNames: this.yAxisDisplayNames, 
          caption: allCaptions 
        });
        } 
      
      if (this.selectedZAxisKey && this.selectedZAxisKey !== null && this.selectedZAxisKey !== undefined) {
        if (isLargeDataset) {
          this.zAxisData = await this.findZAxisDataChunked();
        } else {
          this.zAxisData = this.findZAxisData();
        }
        
        this.zAxisDisplayName = this.getZAxisDisplayNameForEmit();
        this.$emit("z-axis-selected", { 
          data: this.zAxisData, 
          displayName: this.zAxisDisplayName 
        });
        }
      
      
      if (this.selectedColorGroupKey && this.selectedColorGroupKey.length > 0) {
        this.colorGroupData = this.findcolorGroupData();
        
        if (this.selectedChartTypeName != "Histogram") {
          if (!this.yAxisData.length) {
            return;
          }
        }
        if (!this.xAxisData.length) {
          return;
        }
        
        const groupedYAxisData = {};
        const groupedXAxisData = {};
        const groupedZAxisData = {};
        
        this.colorGroupData.forEach(value => {
          groupedYAxisData[value] = [];
          groupedXAxisData[value] = [];
          groupedZAxisData[value] = [];
        });
        
        this.tableData.forEach(row => {
          const colorGroupValue = row[this.selectedColorGroupKey];
          if (this.colorGroupData.includes(colorGroupValue)) {
            const yAxisRowData = this.selectedYAxisKey.map(key => {
              const decimalKey = `${key}_decimal`;
              if (row[decimalKey] !== undefined && row[decimalKey] !== null) {
                const value = parseFloat(row[key]);
                if (!isNaN(value)) {
                  const maxDecimals = Math.min(row[decimalKey], 6);
                  return value.toFixed(maxDecimals);
                }
              }
              return row[key];
            });
            groupedYAxisData[colorGroupValue].push(yAxisRowData);
            
            const xAxisRowData = row[this.selectedXAxisKey];
            groupedXAxisData[colorGroupValue].push(xAxisRowData);
            
            if (this.selectedZAxisKey) {
              const zAxisRowData = row[this.selectedZAxisKey];
              groupedZAxisData[colorGroupValue].push(zAxisRowData);
            }
          }
        });
        
        this.$emit("grouped-y-axis-data", groupedYAxisData);
        this.$emit("grouped-x-axis-data", groupedXAxisData);
        this.$emit("grouped-z-axis-data", groupedZAxisData);
        this.$emit("color-group-key", this.selectedColorGroupKey);
        
        this.colorGroupDisplayName = this.getColorGroupDisplayNameForEmit();
        this.$emit("color-group-display-name", this.colorGroupDisplayName);
        
        if (this.selectedYAxisKey && this.selectedYAxisKey.length > 0) {
          this.yAxisDisplayNames = this.getYAxisDisplayNamesForEmit();
          var allCaptions = this.tableData?.map((row) => row.caption || "");
          
          this.$emit("y-axis-selected", { 
            data: this.yAxisData, 
            displayNames: this.yAxisDisplayNames, 
            caption: allCaptions 
          });
        }
        
        if (this.xAxisScale) {
          this.$emit("x-axis-scale", this.xAxisScale);
        }
        if (this.yAxisScale) {
          this.$emit("y-axis-scale", this.yAxisScale);
        }
        
      } else {
        this.colorGroupData = [];
        this.$emit("grouped-y-axis-data", { groupedYAxisData: [] });
        this.$emit("grouped-x-axis-data", { groupedXAxisData: [] });
        this.$emit("grouped-z-axis-data", { groupedZAxisData: [] });
        
        let savedChartData = JSON.parse(localStorage.getItem("savedChartData"));
        if (savedChartData) {
          savedChartData.GroupColumn = ""
          localStorage.setItem("savedChartData", JSON.stringify(savedChartData));
        }
      }
        
        
        await new Promise(resolve => setTimeout(resolve, 100));
        
        this.showLoaders = false;
        this.$emit("loading-state", this.showLoaders);
        
        this.$nextTick(() => {
          this.$emit("chart-updated");
          
          if (this.xAxisScale) {
            this.$emit("x-axis-scale", this.xAxisScale);
          }
          if (this.yAxisScale) {
            this.$emit("y-axis-scale", this.yAxisScale);
          }
        });
        
      } catch (error) {
        console.error('❌ Error during data extraction:', error);
        this.showLoaders = false;
        this.$emit("loading-state", this.showLoaders);
        this.$emit("sample-error", "Error processing chart data");
      }
    },
    async processLargeDataset(data, processFunction) {
      if (!data || data.length === 0) {
        return [];
      }
      
      if (data.length <= this.dataChunkSize) {
        return processFunction(data);
      }
      
      this.isProcessingLargeData = true;
      this.processedDataCount = 0;
      
      const results = [];
      const totalChunks = Math.ceil(data.length / this.dataChunkSize);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * this.dataChunkSize;
        const end = Math.min(start + this.dataChunkSize, data.length);
        const chunk = data.slice(start, end);
        
        const chunkResult = processFunction(chunk);
        results.push(...chunkResult);
        
        this.processedDataCount = end;
        
        if (i < totalChunks - 1) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
      this.isProcessingLargeData = false;
      this.processedDataCount = 0;
      
      return results;
    },
    
    async findXAxisDataChunked() {
      if (!this.tableData || this.tableData.length === 0) {
        return [];
      }
      
      return await this.processLargeDataset(this.tableData, (chunk) => {
        return chunk.map(row => {
          const decimalKey = `${this.selectedXAxisKey}_decimal`;
          if (row[decimalKey] !== undefined && row[decimalKey] !== null) {
            const value = parseFloat(row[this.selectedXAxisKey]);
            if (!isNaN(value)) {
              const maxDecimals = Math.min(row[decimalKey], 6);
              return value.toFixed(maxDecimals);
            }
          }
            return row[this.selectedXAxisKey];
        });
      });
    },
    
    async findYAxisDataChunked() {
      if (!this.tableData || this.tableData.length === 0) {
        return [];
      }
      
      return await this.processLargeDataset(this.tableData, (chunk) => {
        return this.selectedYAxisKey.map(col => {
          return chunk.map(row => {
            const decimalKey = `${col}_decimal`;
            if (row[decimalKey] !== undefined && row[decimalKey] !== null) {
              const value = parseFloat(row[col]);
              if (!isNaN(value)) {
                const maxDecimals = Math.min(row[decimalKey], 6);
                return value.toFixed(maxDecimals);
              }
            }
              return row[col];
          });
        });
      });
    },
    
    async findZAxisDataChunked() {
      if (!this.tableData || this.tableData.length === 0) {
        return [];
      }
      
      return await this.processLargeDataset(this.tableData, (chunk) => {
        return chunk.map(row => {
          const decimalKey = `${this.selectedZAxisKey}_decimal`;
          if (row[decimalKey] !== undefined && row[decimalKey] !== null) {
            const value = parseFloat(row[this.selectedZAxisKey]);
            if (!isNaN(value)) {
              const maxDecimals = Math.min(row[decimalKey], 6);
              return value.toFixed(maxDecimals);
            }
          }
          return row[this.selectedZAxisKey];
        });
      });
    },
    autoFixColumnNames() {
      if (!this.tableData || !this.tableData.length) {
        return;
      }
      
      const firstRow = this.tableData[0];
      const availableKeys = Object.keys(firstRow);
      
      
      if (this.selectedXAxisKey && !(this.selectedXAxisKey in firstRow)) {
        const fixedName = this.findBestColumnMatch(this.selectedXAxisKey, availableKeys);
        if (fixedName) {
          this.selectedXAxisKey = fixedName;
        }
      }
      
      if (this.selectedYAxisKey && this.selectedYAxisKey.length > 0) {
        this.selectedYAxisKey = this.selectedYAxisKey.map(col => {
          if (!(col in firstRow)) {
            const fixedName = this.findBestColumnMatch(col, availableKeys);
            if (fixedName) {
              return fixedName;
            }
          }
          return col;
        });
      }
      
      if (this.selectedZAxisKey && !(this.selectedZAxisKey in firstRow)) {
        const fixedName = this.findBestColumnMatch(this.selectedZAxisKey, availableKeys);
        if (fixedName) {
          this.selectedZAxisKey = fixedName;
        }
      }
      
      if (this.selectedColorGroupKey && !(this.selectedColorGroupKey in firstRow)) {
        const fixedName = this.findBestColumnMatch(this.selectedColorGroupKey, availableKeys);
        if (fixedName) {
          this.selectedColorGroupKey = fixedName;
        }
      }
    },
    
    findBestColumnMatch(selectedName, availableKeys) {
  const searchNames = Array.isArray(selectedName) 
    ? selectedName 
    : [selectedName];

  const safeToLower = (str) => String(str || '').toLowerCase();

  for (const name of searchNames) {
    const currentSearch = safeToLower(name);

    const exactMatch = availableKeys.find(key => 
      safeToLower(key) === currentSearch
    );
    if (exactMatch) return exactMatch;

    const partialMatch = availableKeys.find(key => {
      const lowerKey = safeToLower(key);
      return (
        lowerKey.includes(currentSearch) ||
        currentSearch.includes(lowerKey)
      );
    });
    if (partialMatch) return partialMatch;

    const cleanSelected = currentSearch.replace(/[^a-zA-Z0-9]/g, '');
    const cleanMatch = availableKeys.find(key => {
      const cleanKey = safeToLower(key).replace(/[^a-zA-Z0-9]/g, '');
      return (
        cleanKey.includes(cleanSelected) || 
        cleanSelected.includes(cleanKey)
      );
    });
    if (cleanMatch) return cleanMatch;
  }

  return null;
},
    
    async populateEditData(editData) {
      
      if (!editData) {
        this.isPopulatingEditData = false; // Reset flag
        return;
      }
      
      if (this.isPopulatingEditData) {
        console.warn('populateEditData: Already populating, skipping. Will retry after delay...');
        console.warn('Current flag state:', this.isPopulatingEditData);
        const retryDelay = 500;
        setTimeout(() => {
          if (!this.isPopulatingEditData && editData) {
            this.populateEditData(editData);
          } else if (this.isPopulatingEditData) {
            console.warn('populateEditData: Still populating after delay, forcing reset and retry');
            this.isPopulatingEditData = false;
            this.$nextTick(() => {
              this.populateEditData(editData);
            });
          }
        }, retryDelay);
        return;
      }
      
      this.isPopulatingEditData = true;
      
      let timeoutId = setTimeout(() => {
        if (this.isPopulatingEditData) {
          console.warn('populateEditData: Timeout reached, resetting flag');
          this.isPopulatingEditData = false;
        }
      }, 10000); // 10 second timeout
      
      try {
        if (editData.tableData?.length) {
          this.tableData = editData.tableData;
          this.$emit("table-data", editData.tableData);
        }
        if (editData.xAxisData?.length) this.xAxisData = editData.xAxisData;
        if (editData.yAxisData?.length) this.yAxisData = editData.yAxisData;
        if (editData.zAxisData?.length) this.zAxisData = editData.zAxisData;
        if (editData.Filter && Object.keys(editData.Filter).length > 0) {
          this.savedFilterData = editData.Filter;
          this.filterFromChart = editData.Filter;
        }

        this.internalChartTitle = editData.chartName || editData.plotName || '';
        this.xBins = editData.xBins || 20;
        this.yBins = editData.yBins || 20;
        this.colorGroupKey = editData.colorGroupKey || '';
        this.xAxisScale = editData.xAxisScale || 'linear';
        this.yAxisScale = editData.yAxisScale || 'linear';
        this.isXLog = editData.xAxisScale === 'log';
        this.isYLog = editData.yAxisScale === 'log';
        
        this.xAxisDisplayName = editData.xAxisDisplayName || '';
        this.yAxisDisplayNames = Array.isArray(editData.yAxisDisplayNames) ? [...editData.yAxisDisplayNames] : [];
        this.zAxisDisplayName = editData.zAxisDisplayName || '';
        this.colorGroupDisplayName =
          editData.colorGroupDisplayName ||
          editData.GroupColumnDisplayName ||
          '';
        
        this.xAxisData = Array.isArray(editData.xAxisData) ? [...editData.xAxisData] : [];
        this.yAxisData = Array.isArray(editData.yAxisData) ? [...editData.yAxisData] : [];
        this.zAxisData = Array.isArray(editData.zAxisData) ? [...editData.zAxisData] : [];
        
        this.xColorGroupData = editData.xColorGroupData && typeof editData.xColorGroupData === 'object' ? { ...editData.xColorGroupData } : {};
        this.yColorGroupData = editData.yColorGroupData && typeof editData.yColorGroupData === 'object' ? { ...editData.yColorGroupData } : {};
        this.zColorGroupData = editData.zColorGroupData && typeof editData.zColorGroupData === 'object' ? { ...editData.zColorGroupData } : {};
        
        this.isPublic = editData.isShared || false;
        this.accessLevel = editData.isShared ? 'public' : 'private';
        
        if (editData.TableType) {
          const xAxisColumnToRestore = editData.XAxisColumn;
          const yAxisKeysToRestore = parseYAxisKeysFromChart({
            YAxisColumn: editData.YAxisColumn,
            YAxisColumns: editData.YAxisColumns,
            GroupColumn: editData.GroupColumn || editData.colorGroupKey || "",
          });
          const zAxisColumnToRestore = editData.ZAxisColumn;
          const groupColumnToRestore = editData.GroupColumn || editData.colorGroupKey;
          
          if (!this.tableTypesNames || this.tableTypesNames.length === 0) {
            await this.getTableTypes();
            await this.$nextTick();
          }
          
          let tableTypeToSet = editData.TableType;
          
          if (this.tableTypesNames && this.tableTypesNames.length > 0) {
            const found = this.tableTypesNames.find(item => item === editData.TableType);
            if (found) {
              tableTypeToSet = found;
            } else {
              const foundCaseInsensitive = this.tableTypesNames.find(item => 
                String(item).toLowerCase() === String(editData.TableType).toLowerCase()
              );
              if (foundCaseInsensitive) {
                tableTypeToSet = foundCaseInsensitive;
              } else {
                console.warn('No match found for table type:', editData.TableType);
              }
            }
          }
          
          this.selectedTableTypeName = tableTypeToSet;
          
          await this.$nextTick();
          
          await this.tableTypeChange(tableTypeToSet);
          
          if (this.selectedTableTypeName !== tableTypeToSet) {
            console.warn('selectedTableTypeName was reset, setting it again');
            this.selectedTableTypeName = tableTypeToSet;
            await this.$nextTick();
          }
          
          if (editData.chartType) {
            if (!this.chartTypesNames || this.chartTypesNames.length === 0) {
              await this.getChartTypes();
              await this.$nextTick();
            }
            
            const chartTypeString = this.chartTypesNames.find(item => 
              item === editData.chartType || 
              String(item).toLowerCase() === String(editData.chartType).toLowerCase()
            ) || editData.chartType;
            
            this.selectedChartTypeName = chartTypeString;
            
            await this.$nextTick();
            
            this.chartTypeChange(chartTypeString);
            
            if (this.selectedChartTypeName !== chartTypeString) {
              console.warn('selectedChartTypeName was reset, setting it again');
              this.selectedChartTypeName = chartTypeString;
              await this.$nextTick();
            }
            
            this.$forceUpdate();
            await this.$nextTick();
            
            if (this.xAxisScale) {
              this.$emit("x-axis-scale", this.xAxisScale);
            }
            if (this.yAxisScale) {
              this.$emit("y-axis-scale", this.yAxisScale);
            }
          }
          
          await this.$nextTick();
          await new Promise(resolve => setTimeout(resolve, 500));
          
          if (this.internalChartTitle) {
            this.$emit("chart-title", this.internalChartTitle);
          }
          
          if (this.xAxisItems.length > 0) {
            this.filteredXAxisItems = [...this.xAxisItems];
          }
          if (this.yAxisItems.length > 0) {
            this.filteredYAxisItems = [...this.yAxisItems];
          }
          if (this.zAxisItems.length > 0) {
            this.filteredZAxisItems = [...this.zAxisItems];
          }
          if (this.colorGroupItems.length > 0) {
            this.filteredColorGroupItems = [...this.colorGroupItems];
          }
          
          this.$forceUpdate();
          await this.$nextTick();
          
          this.xAxisChanged = false;
          this.yAxisChanged = false;
          this.zAxisChanged = false;
          this.axisSelected = false;
          
          const isEditOrEmbedded = this.isEditing || this.embedded;
          
          if (xAxisColumnToRestore && this.xAxisItems && this.xAxisItems.length > 0) {
            const xAxisItem = this.xAxisItems.find(item => item.name === xAxisColumnToRestore);
            const filteredXAxisItem = this.filteredXAxisItems.find(item => item.name === xAxisColumnToRestore);
            
            if (xAxisItem && filteredXAxisItem) {
              if (!filteredXAxisItem) {
                console.warn('X-Axis item not in filtered array, adding it');
                this.filteredXAxisItems.push(xAxisItem);
              }
              
              if (isEditOrEmbedded) {
                this.selectedXAxisKey = null;
                await this.$nextTick();
              }
              
              this.selectedXAxisKey = xAxisItem.name;
            } else {
              const caseInsensitiveItem = this.xAxisItems.find(item => 
                item.name.toLowerCase() === xAxisColumnToRestore.toLowerCase()
              );
              if (caseInsensitiveItem) {
                if (isEditOrEmbedded) {
                  this.selectedXAxisKey = null;
                  await this.$nextTick();
                }
                this.selectedXAxisKey = caseInsensitiveItem.name;
              } else {
                if (isEditOrEmbedded) {
                  this.selectedXAxisKey = null;
                  await this.$nextTick();
                }
                this.selectedXAxisKey = xAxisColumnToRestore;
                console.warn('X-Axis item not found, setting value directly:', xAxisColumnToRestore);
              }
            }
          } else if (xAxisColumnToRestore) {
            this.selectedXAxisKey = xAxisColumnToRestore;
          }
          
          if (yAxisKeysToRestore.length > 0 && this.yAxisItems && this.yAxisItems.length > 0) {
            const expectedKeys = yAxisKeysToRestore.map((colName) => {
              const yAxisItem = this.yAxisItems.find((item) => item.name === colName);
              const filteredYAxisItem = this.filteredYAxisItems.find((item) => item.name === colName);
              if (yAxisItem && !filteredYAxisItem) {
                this.filteredYAxisItems.push(yAxisItem);
              }
              return yAxisItem ? yAxisItem.name : colName;
            }).filter((key) => key);

            if (expectedKeys.length > 0) {
              if (isEditOrEmbedded) {
                this.selectedYAxisKey = [];
                await this.$nextTick();
              }
              this.selectedYAxisKey = this.filterLegendFromYAxis([...new Set(expectedKeys)]);
            }
          } else if (yAxisKeysToRestore.length > 0) {
            this.selectedYAxisKey = this.filterLegendFromYAxis([...yAxisKeysToRestore]);
          }
          
          if (zAxisColumnToRestore && this.zAxisItems && this.zAxisItems.length > 0) {
            const zAxisItem = this.zAxisItems.find(item => item.name === zAxisColumnToRestore);
            const filteredZAxisItem = this.filteredZAxisItems.find(item => item.name === zAxisColumnToRestore);
            
            if (zAxisItem && filteredZAxisItem) {
              if (!filteredZAxisItem) {
                this.filteredZAxisItems.push(zAxisItem);
              }
              
              if (isEditOrEmbedded) {
                this.selectedZAxisKey = null;
                await this.$nextTick();
              }
              
              this.selectedZAxisKey = zAxisItem.name;
            } else {
              const caseInsensitiveItem = this.zAxisItems.find(item => 
                item.name.toLowerCase() === zAxisColumnToRestore.toLowerCase()
              );
              if (caseInsensitiveItem) {
                if (isEditOrEmbedded) {
                  this.selectedZAxisKey = null;
                  await this.$nextTick();
                }
                this.selectedZAxisKey = caseInsensitiveItem.name;
              } else {
                if (isEditOrEmbedded) {
                  this.selectedZAxisKey = null;
                  await this.$nextTick();
                }
                this.selectedZAxisKey = zAxisColumnToRestore;
                console.warn('Z-Axis item not found, setting value directly:', zAxisColumnToRestore);
              }
            }
          } else if (zAxisColumnToRestore) {
            this.selectedZAxisKey = zAxisColumnToRestore;
          }
          
          if (groupColumnToRestore && this.colorGroupItems && this.colorGroupItems.length > 0) {
            const groupItem = this.colorGroupItems.find(item => item.name === groupColumnToRestore);
            const filteredGroupItem = this.filteredColorGroupItems.find(item => item.name === groupColumnToRestore);
            
            if (groupItem && filteredGroupItem) {
              if (!filteredGroupItem) {
                this.filteredColorGroupItems.push(groupItem);
              }
              
              if (isEditOrEmbedded) {
                this.selectedColorGroupKey = null;
                await this.$nextTick();
              }
              
              this.selectedColorGroupKey = groupItem.name;
            } else {
              const caseInsensitiveItem = this.colorGroupItems.find(item => 
                item.name.toLowerCase() === groupColumnToRestore.toLowerCase()
              );
              if (caseInsensitiveItem) {
                if (isEditOrEmbedded) {
                  this.selectedColorGroupKey = null;
                  await this.$nextTick();
                }
                this.selectedColorGroupKey = caseInsensitiveItem.name;
              } else {
                if (isEditOrEmbedded) {
                  this.selectedColorGroupKey = null;
                  await this.$nextTick();
                }
                this.selectedColorGroupKey = groupColumnToRestore;
                console.warn('Color Group item not found, setting value directly:', groupColumnToRestore);
              }
            }
          } else if (groupColumnToRestore) {
            this.selectedColorGroupKey = groupColumnToRestore;
          }
          
          if (this.selectedXAxisKey && this.filteredXAxisItems.length > 0) {
            const matchingXItem = this.filteredXAxisItems.find(item => item.name === this.selectedXAxisKey);
            if (matchingXItem) {
              void 0;
            } else {
              console.warn('✗ X-Axis value NOT found in filtered array:', this.selectedXAxisKey);
              const xItem = this.xAxisItems.find(item => item.name === this.selectedXAxisKey);
              if (xItem) {
                this.filteredXAxisItems.push(xItem);
              }
            }
          }
          
          if (this.selectedZAxisKey && this.filteredZAxisItems.length > 0) {
            const matchingZItem = this.filteredZAxisItems.find(item => item.name === this.selectedZAxisKey);
            if (matchingZItem) {
              void 0;
            } else {
              console.warn('✗ Z-Axis value NOT found in filtered array:', this.selectedZAxisKey);
              const zItem = this.zAxisItems.find(item => item.name === this.selectedZAxisKey);
              if (zItem) {
                this.filteredZAxisItems.push(zItem);
              }
            }
          }
          
          if (this.selectedColorGroupKey && this.filteredColorGroupItems.length > 0) {
            const matchingGroupItem = this.filteredColorGroupItems.find(item => item.name === this.selectedColorGroupKey);
            if (matchingGroupItem) {
              void 0;
            } else {
              console.warn('✗ Color Group value NOT found in filtered array:', this.selectedColorGroupKey);
              const groupItem = this.colorGroupItems.find(item => item.name === this.selectedColorGroupKey);
              if (groupItem) {
                this.filteredColorGroupItems.push(groupItem);
              }
            }
          }
          
          if (this.selectedYAxisKey && Array.isArray(this.selectedYAxisKey) && this.selectedYAxisKey.length > 0 && this.filteredYAxisItems.length > 0) {
            const missingYItems = this.selectedYAxisKey.filter(key => 
              !this.filteredYAxisItems.find(item => item.name === key)
            );
            if (missingYItems.length === 0) {
              void 0;
            } else {
              console.warn('✗ Some Y-Axis values NOT found in filtered array:', missingYItems);
              missingYItems.forEach(key => {
                const yItem = this.yAxisItems.find(item => item.name === key);
                if (yItem) {
                  this.filteredYAxisItems.push(yItem);
                }
              });
            }
          }
          
          await this.$nextTick();
          this.$forceUpdate();
          await this.$nextTick();
          this.$forceUpdate();
          await this.$nextTick();
          
          
          await this.refreshAxisEmitsFromTableData();
          this.processColorGroupDataForEditMode();
          this.resetUserSelectionFlag();

          setTimeout(() => {
            this.$forceUpdate();
          }, 100);
        } else {
          const isEditOrEmbedded = this.isEditing || this.embedded;
          
          if (this.xAxisItems.length > 0) {
            this.filteredXAxisItems = [...this.xAxisItems];
          }
          if (this.yAxisItems.length > 0) {
            this.filteredYAxisItems = [...this.yAxisItems];
          }
          if (this.zAxisItems.length > 0) {
            this.filteredZAxisItems = [...this.zAxisItems];
          }
          if (this.colorGroupItems.length > 0) {
            this.filteredColorGroupItems = [...this.colorGroupItems];
          }
          
          if (editData.XAxisColumn && this.xAxisItems && this.xAxisItems.length > 0) {
            const xAxisItem = this.xAxisItems.find(item => item.name === editData.XAxisColumn);
            const filteredXAxisItem = this.filteredXAxisItems.find(item => item.name === editData.XAxisColumn);
            if (xAxisItem && filteredXAxisItem && (!this.selectedXAxisKey || this.selectedXAxisKey === '' || (Array.isArray(this.selectedXAxisKey) && this.selectedXAxisKey.length === 0))) {
              if (isEditOrEmbedded) {
                this.selectedXAxisKey = null;
                await this.$nextTick();
              }
              this.selectedXAxisKey = xAxisItem.name;
              await this.$nextTick();
              this.$forceUpdate();
              await this.$nextTick();
              await this.handleXAxisSelection();
            } else if (xAxisItem && !filteredXAxisItem) {
              this.filteredXAxisItems.push(xAxisItem);
              if (isEditOrEmbedded) {
                this.selectedXAxisKey = null;
                await this.$nextTick();
              }
              this.selectedXAxisKey = xAxisItem.name;
              await this.$nextTick();
              this.$forceUpdate();
            }
          } else if (editData.XAxisColumn && !this.selectedXAxisKey) {
            this.selectedXAxisKey = editData.XAxisColumn;
            await this.$nextTick();
            this.$forceUpdate();
          }
          
          const yKeysFromEdit = parseYAxisKeysFromChart({
            YAxisColumn: editData.YAxisColumn,
            YAxisColumns: editData.YAxisColumns,
            GroupColumn: editData.GroupColumn || editData.colorGroupKey || "",
          });
          if (yKeysFromEdit.length > 0 && this.yAxisItems && this.yAxisItems.length > 0) {
            const expectedKeys = yKeysFromEdit.map((colName) => {
              const yAxisItem = this.yAxisItems.find(item => item.name === colName);
              const filteredYAxisItem = this.filteredYAxisItems.find(item => item.name === colName);
              if (yAxisItem && !filteredYAxisItem) {
                this.filteredYAxisItems.push(yAxisItem);
              }
              return yAxisItem ? yAxisItem.name : null;
            }).filter(key => key !== null);
            
            if (expectedKeys.length > 0 && this.selectedYAxisKey.length === 0) {
              if (isEditOrEmbedded) {
                this.selectedYAxisKey = [];
                await this.$nextTick();
              }
              this.selectedYAxisKey = [...new Set(expectedKeys)];
              await this.$nextTick();
              await this.handleYAxisSelection();
            }
          }
          
          if (editData.ZAxisColumn && this.zAxisItems && this.zAxisItems.length > 0) {
            const zAxisItem = this.zAxisItems.find(item => item.name === editData.ZAxisColumn);
            const filteredZAxisItem = this.filteredZAxisItems.find(item => item.name === editData.ZAxisColumn);
            if (zAxisItem && filteredZAxisItem && (!this.selectedZAxisKey || this.selectedZAxisKey === '' || (Array.isArray(this.selectedZAxisKey) && this.selectedZAxisKey.length === 0))) {
              if (isEditOrEmbedded) {
                this.selectedZAxisKey = null;
                await this.$nextTick();
              }
              this.selectedZAxisKey = zAxisItem.name;
              await this.$nextTick();
              this.$forceUpdate();
              await this.$nextTick();
              await this.handleZAxisSelection();
            } else if (zAxisItem && !filteredZAxisItem) {
              this.filteredZAxisItems.push(zAxisItem);
              if (isEditOrEmbedded) {
                this.selectedZAxisKey = null;
                await this.$nextTick();
              }
              this.selectedZAxisKey = zAxisItem.name;
              await this.$nextTick();
              this.$forceUpdate();
            }
          } else if (editData.ZAxisColumn && !this.selectedZAxisKey) {
            this.selectedZAxisKey = editData.ZAxisColumn;
            await this.$nextTick();
            this.$forceUpdate();
          }
          
          if (editData.GroupColumn && this.colorGroupItems && this.colorGroupItems.length > 0) {
            const groupItem = this.colorGroupItems.find(item => item.name === editData.GroupColumn);
            const filteredGroupItem = this.filteredColorGroupItems.find(item => item.name === editData.GroupColumn);
            if (groupItem && filteredGroupItem && !this.selectedColorGroupKey) {
              if (isEditOrEmbedded) {
                this.selectedColorGroupKey = null;
                await this.$nextTick();
              }
              this.selectedColorGroupKey = groupItem.name;
              await this.$nextTick();
              this.handleColorGroupSelection();
            } else if (groupItem && !filteredGroupItem) {
              this.filteredColorGroupItems.push(groupItem);
              if (isEditOrEmbedded) {
                this.selectedColorGroupKey = null;
                await this.$nextTick();
              }
              this.selectedColorGroupKey = groupItem.name;
              await this.$nextTick();
            }
          }
        }
      } catch (error) {
        console.error('Error populating edit data:', error);
        console.error('Error stack:', error.stack);
        const msg =
          error?.message ||
          (typeof error === "string" ? error : "Failed to load plot for editing. Please try again.");
        this.showSnackbar(msg, "error");
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        
        const initialChartData = {
          ChartType: this.selectedChartTypeName || editData.chartType || '',
          TableType: this.selectedTableTypeName || editData.TableType || '',
          XAxisColumn: this.selectedXAxisKey || editData.XAxisColumn || '',
          YAxisColumn: buildYAxisColumnString(
            this.selectedYAxisKey,
            this.selectedColorGroupKey || editData.GroupColumn || editData.colorGroupKey || ""
          ),
          YAxisColumns: buildYAxisColumnsArray(
            this.selectedYAxisKey,
            this.selectedColorGroupKey || editData.GroupColumn || editData.colorGroupKey || ""
          ),
          ZAxisColumn: this.selectedZAxisKey || editData.ZAxisColumn || '',
          GroupColumn: this.selectedColorGroupKey || editData.GroupColumn || '',
          NumXBins: this.xBins || editData.xBins || null,
          NumYBins: this.yBins || editData.yBins || null,
          XAxisScale: this.xAxisScale || editData.xAxisScale || 'linear',
          YAxisScale: this.yAxisScale || editData.yAxisScale || 'linear',
          AccessLevel: this.accessLevel || (editData.isShared ? 'public' : 'private'),
          Filter: editData.Filter || {}
        };
        localStorage.setItem("initialChartData", JSON.stringify(initialChartData));
        
        this.isUpdateEnabled = this.checkIfUpdateEnabled();

        if (this.tableData?.length) {
          await this.emitAxisDisplayNamesFromTableData();
        }
        
        this.isPopulatingEditData = false;
        
        
        await this.$nextTick();
        this.$forceUpdate();
        await this.$nextTick();
        this.$forceUpdate();
        await this.$nextTick();
        
        setTimeout(() => {
          this.$forceUpdate();
          
        }, 200);
      }
      
      if (this.selectedChartTypeName) {
        this.$emit('chart-type-changed', this.selectedChartTypeName);
      }
      
      if (this.xAxisScale) {
        this.$emit('x-axis-scale', this.xAxisScale);
      }
      if (this.yAxisScale) {
        this.$emit('y-axis-scale', this.yAxisScale);
      }
      
      
    },
    
    forceResetPopulatingFlag() {
      console.warn('forceResetPopulatingFlag: Manually resetting isPopulatingEditData flag');
      this.isPopulatingEditData = false;
    },
    
    handleDeletePlotEdit() {
      if (confirm('Are you sure you want to delete this plot?')) {
        this.deleteChart();
        this.$emit('plot-deleted');
      }
    },
    
    handleSaveEditMode() {
      this.createLoading = true;
      
      this.createChart();
      
    },
    processColorGroupDataForEditMode() {
      
      if (this.selectedColorGroupKey && this.selectedColorGroupKey.length > 0) {
        this.colorGroupData = this.findcolorGroupData();
        
        if (this.colorGroupData && this.colorGroupData.length > 0) {
          
          const groupedYAxisData = {};
          const groupedXAxisData = {};
          const groupedZAxisData = {};
          
          this.colorGroupData.forEach(value => {
            groupedYAxisData[value] = [];
            groupedXAxisData[value] = [];
            groupedZAxisData[value] = [];
          });
          
          this.tableData.forEach(row => {
            const colorGroupValue = row[this.selectedColorGroupKey];
            if (this.colorGroupData.includes(colorGroupValue)) {
              const yAxisRowData = this.selectedYAxisKey.map(key => {
                const decimalKey = `${key}_decimal`;
                if (row[decimalKey] !== undefined && row[decimalKey] !== null) {
                  const value = parseFloat(row[key]);
                  if (!isNaN(value)) {
                    const maxDecimals = Math.min(row[decimalKey], 6);
                    return value.toFixed(maxDecimals);
                  }
                }
                return row[key];
              });
              groupedYAxisData[colorGroupValue].push(yAxisRowData);
              
              const xAxisRowData = row[this.selectedXAxisKey];
              groupedXAxisData[colorGroupValue].push(xAxisRowData);
              
              if (this.selectedZAxisKey) {
                const zAxisRowData = row[this.selectedZAxisKey];
                groupedZAxisData[colorGroupValue].push(zAxisRowData);
              }
            }
          });
          
          this.$emit("grouped-y-axis-data", groupedYAxisData);
          this.$emit("grouped-x-axis-data", groupedXAxisData);
          this.$emit("grouped-z-axis-data", groupedZAxisData);
          this.$emit("color-group-key", this.selectedColorGroupKey);
          
          this.colorGroupDisplayName = this.getColorGroupDisplayNameForEmit();
          this.$emit("color-group-display-name", this.colorGroupDisplayName);
          
          if (this.xAxisScale) {
            this.$emit("x-axis-scale", this.xAxisScale);
          }
          if (this.yAxisScale) {
            this.$emit("y-axis-scale", this.yAxisScale);
          }
          
        } else {
          this.$emit("grouped-y-axis-data", { groupedYAxisData: [] });
          this.$emit("grouped-x-axis-data", { groupedXAxisData: [] });
          this.$emit("grouped-z-axis-data", { groupedZAxisData: [] });
        }
      } else {
        this.$emit("grouped-y-axis-data", { groupedYAxisData: [] });
        this.$emit("grouped-x-axis-data", { groupedXAxisData: [] });
        this.$emit("grouped-z-axis-data", { groupedZAxisData: [] });
      }
    },
  },
  async mounted() {
    
    localStorage.setItem("lastView", "/Fluidsdata-studio");
    
    this.isUpdateEnabled = this.checkIfUpdateEnabled();
    localStorage.removeItem("newsavedChartData");
    await this.getChartTypes();
    this.getTableTypes();
    this.getSavedCharts();
    
    
    const pathSegments = this.$route.path.split('/').filter(Boolean); // Remove empty segments
    const mainRoute = pathSegments.length > 1 ? pathSegments[1].toLowerCase() : null;

    if (mainRoute === 'fluidsdata-studio') {
      const savedChartData = localStorage.getItem("savedChartData");
      if (savedChartData) {
        const chartData = JSON.parse(savedChartData);
        if (chartData && chartData.ID) {
          this.selectedChartId = chartData.ID;
          this.savedChartChangeLoad(chartData);
        }
      } 
    }
    
    await this.$nextTick();
    if (this.isEditing && this.editPlotData && !this.isPopulatingEditData) {
      this.populateEditData(this.editPlotData);
    }
    
  },
  beforeUnmount() {
    this.cancelAllRequests();
  },
  beforeUnmount() {
    this.cancelAllRequests();
  },
};
</script>

<style scoped>
.filter-wrapper.studio-new {
  width: 320px;
  min-width: 320px;
  max-width: 320px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 8px rgba(0,0,0,0.03);
  z-index: 2;
  overflow-y: auto;
  padding: 16px;
}

.text-truncate {
  overflow: visible !important;
  text-overflow: unset !important;
  white-space: nowrap !important;
  max-width: none !important;
  min-width: 120px;
  display: block;
  font-size: 11.50px;
}

.v-select .v-field__input {
  overflow: visible !important;
}

.v-select .v-field__input .text-truncate {
  max-width: none !important;
  min-width: 100px;
  font-size: 11.50px;
  overflow: visible !important;
  text-overflow: unset !important;
  white-space: nowrap !important;
}

.dropdown-item {
  width: 100%;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 11.50px;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.dropdown-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.dropdown-item .text-truncate {
  max-width: 100%;
  min-width: 120px;
  width: 100%;
  font-size: 11.50px;
}

.v-select .v-field__input {
  font-size: 11.50px;
  overflow: visible !important;
  text-overflow: unset !important;
  white-space: nowrap !important;
}

/* Ensure v-select input can display full text */
.v-select .v-field__input input {
  overflow: visible !important;
  text-overflow: unset !important;
  white-space: nowrap !important;
  max-width: none !important;
}

/* Ensure the v-select field itself can expand */
.v-select .v-field {
  min-width: 0 !important;
  flex: 1 !important;
}

.custom-field.v-select {
  min-width: 0 !important;
  flex: 1 !important;
}

.v-select .v-select__selection-text {
  font-size: 11.50px;
}

.v-list-item-title {
  font-size: 11.50px;
}

.studio-multiselect-item .v-list-item-title {
  font-size: 11.50px;
}

.studio-multiselect-item .text-truncate {
  max-width: 100%;
  min-width: 120px;
  width: 100%;
  font-size: 11.50px;
}

.dropdown-item .v-list-item-title {
  font-size: 11.50px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Additional styling for form elements in sidebar */
.studio-btns {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.studio-btns .save-btn,
.studio-btns .delete-btn {
  flex: 1;
  height: 36px;
  font-size: 13.50px;
}

.tab-icon-div {
  margin-bottom: 16px;
}

.studio-input-form {
  width: 100%;
}

.studio-top-heading {
  margin-bottom: 16px;
  width: 100%;
}

.studio-top-heading .plot-title,
.studio-top-heading h6 {
  font-size: 17.50px;
  font-weight: 600;
  color: #222;
  margin: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filter-wrapper.studio-new {
    width: 280px;
    min-width: 280px;
    max-width: 280px;
  }
}

/* Slider styles for bins inputs */
.slider {
  -webkit-appearance: none;
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #e0e0e0;
  outline: none;
  opacity: 0.7;
  transition: opacity 0.2s;
  margin-top: 8px;
}

.slider:hover {
  opacity: 1;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1976d2;
  cursor: pointer;
}

.slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1976d2;
  cursor: pointer;
  border: none;
}

/* Loader styles */
.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
}

.loaders {
  width: 20px !important;
  height: 20px !important;
}

/* Removed back button styles - now handled by top bar */

/* Form spacing styles */
.studio-input-form .v-col {
  margin-bottom: 8px;
}

.studio-input-form label {
  display: block;
  margin-bottom: 4px;
  font-size: 13.50px;
  font-weight: 500;
  color: #333;
}

.studio-input-form .custom-field {
  margin-bottom: 8px;
}

/* Scale toggle spacing */
.tab-div {
  margin-left: 8px;
  margin-bottom: 5px;
}

.axis-toggle {
  height: 32px;
}

.switch-btn {
  font-size: 11.50px;
  padding: 0 8px;
  min-width: 60px;
}
</style>